import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectorRef, Input, Output, EventEmitter, ElementRef, NgZone, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { forkJoin } from 'rxjs';
import { AdminService } from 'src/app/service/admin.service';
import { Event } from 'src/app/model/event';
import { SubmissionsService } from 'src/app/service/submissions.service';
import { Review, Submission } from 'src/app/model/paper';
import { SubmissionsFilter } from 'src/app/enum/submissions.filter';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { EventsService } from 'src/app/service/events.service';
import { SelectOption } from 'src/app/model/select.option';
import { SubmissionStatus } from 'src/app/enum/submission.status';
import { dictControlFields, specialFields, columnsOrder, dictControlFilters, groupFields, publicationFields, specialColumnsFields, ColorSchemeFilter } from 'src/app/model/submissionFieldFilter';
import { MatSort } from '@angular/material/sort';
import { Rank } from 'src/app/class/rank';
import { ReviewStatus } from 'src/app/enum/review.status';
import { plainToClass } from 'class-transformer';
import { FormFieldChoice } from 'src/app/model/form.field.choice';
import { Form } from 'src/app/model/form';
import { FormField } from 'src/app/model/form.field';
import { arrayHasElements, shallowCopyArray, isEqual, intersectionArrays, isValidNumber } from 'src/app/utils/utils';
import { ReviewsConfigurationService } from 'src/app/service/reviews.configuration.service';
import { EventReviewConfiguration } from 'src/app/model/eventReviewConfiguration';
import { PopoverComponent } from 'src/app/component/popover/popover.component';
import { ScrollTopFabComponent } from 'src/app/component/scroll-top-fab/scroll-top-fab.component';
import { MenuSubmissionListComponent } from 'src/app/component/menu-submission-list/menu-submission-list.component';
import { defaultFields } from 'src/app/enum/submissions.field';
import { PublicationService } from 'src/app/service/publication.service';
import { CheckListField } from 'src/app/model/checklist-field';
import { File } from 'src/app/model/file';
import { FileRules } from 'src/app/model/file.rules';
import { FormFieldCategory } from 'src/app/enum/form-field-category';
import { ExportService } from 'src/app/service/export.service';
import { CountryPipe } from 'src/app/pipe/country.pipe';
import { PreferencesService } from 'src/app/service/preferences.service';
import { NotificationService } from 'src/app/service/notification.service';
import { Router } from '@angular/router';
import { PageViewMode } from 'src/app/utils/page-view-mode';
import { TranslateService } from '@ngx-translate/core';
import { FilesService } from 'src/app/service/files.service';
import { buildXlsxBlob, XlsxRow } from 'src/app/utils/xlsx-export';

export interface SubmissionRanking {
  submission: Submission;
  rank: Rank;
  selected: boolean;
  // Classe de cor da linha PRÉ-COMPUTADA (status ou publicação) — evita chamar
  // getColorSchemeClass() 2× por linha por CD no template. Recomputada quando o esquema de
  // cor, o status ou a prontidão de publicação mudam (recomputeColorClasses).
  colorClass?: string;
}

@Component({
  selector: 'app-submissions-list-table-v2[page]',
  templateUrl: './submissions-list-table-v2.component.html',
  styleUrls: ['./submissions-list-table-v2.component.scss'],
  // OnPush (P0.3 perf): a árvore só é re-checada em evento do template, mudança de @Input ou
  // markForCheck() explícito. TODAS as mutações de estado assíncronas (subscribes de dados/
  // config, setTimeouts, ResizeObserver) chamam this.cdr.markForCheck() — senão a UI trava.
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubmissionsListTableV2Component implements OnInit, OnDestroy {
  @Input() private readonly page: string;

  /** True quando a tabela é renderizada no contexto "Status de publicação"
   *  (rota `publication/status`). Usado no template pra tornar o pill de
   *  Status read-only — decisão editorial não pertence a este contexto. */
  public get isPublicationMode(): boolean { return this.page === 'publication'; }

  // Permite ao componente-pai esconder a busca interna (a página de ranking
  // renderiza a busca no próprio header, ao lado do título). Default true mantém
  // o comportamento das demais páginas que reusam a tabela (publication-status).
  @Input() public showInternalSearch: boolean = true;

  // Mostra um botão "..." no cabeçalho da coluna de numeração (position) que
  // dispara o menu de seleção de colunas do pai. Default false (só a ranking usa).
  @Input() public showColumnsMenuButton: boolean = false;
  // Estado aberto do menu de colunas (controlado pelo pai). Destaca o ícone do
  // botão enquanto o menu está aberto.
  @Input() public columnsMenuOpen: boolean = false;
  // Emite a origem (o próprio botão) para o pai ancorar o overlay do menu nele.
  @Output() public columnsMenuToggle = new EventEmitter<CdkOverlayOrigin>();
  @ViewChild('columnsMenuOrigin', { read: CdkOverlayOrigin }) public columnsMenuOrigin: CdkOverlayOrigin;

  // Cabeçalho flutuante (frozen header): o card é o scroll container horizontal; o
  // clone do cabeçalho gruda no topo (abaixo do cabeçalho da página) quando o
  // cabeçalho real rola para fora, com a posição horizontal sincronizada.
  @ViewChild('tableScroll', { read: ElementRef }) private tableScrollRef: ElementRef<HTMLElement>;
  @ViewChild('floatingHeader') private floatingHeaderRef: ElementRef<HTMLElement>;

  // ===== Threshold band da coluna Span (hover no cabeçalho "Span") =====
  // Popover ancorado no <th> do cabeçalho (capturado no mouseenter). Editar o band
  // ajusta spanLimitA/B do eventReviewConfiguration → a coluna recolore ao vivo
  // (rankingSpanClass lê esses limites) e os valores persistem com debounce.
  @ViewChild('spanPop') private spanPop: PopoverComponent;
  public spanAnchor: HTMLElement | null = null;
  private spanHoverHideTimer: ReturnType<typeof setTimeout> | null = null;
  private spanPersistTimer: ReturnType<typeof setTimeout> | null = null;

  // Barra de ações de seleção + botões Baixar/Exportar: fixas e centralizadas na área
  // VISÍVEL do card durante o scroll horizontal. A largura de cada host = card.clientWidth
  // (a parte visível); o conteúdo interno centraliza (margin auto / justify-content). Os
  // setters disparam o ajuste quando os elementos aparecem (vivem sob *ngIf de dados).
  private actionBarRef: ElementRef<HTMLElement> | null = null;
  private exportButtonsRef: ElementRef<HTMLElement> | null = null;
  private cardResizeObserver: any = null;
  private roScheduled = false;   // debounce do ResizeObserver (coalesce rajada de disparos num rAF)
  @ViewChild('actionBar', { read: ElementRef })
  set actionBar(ref: ElementRef<HTMLElement> | undefined) {
    this.actionBarRef = ref || null;
    if (ref) { this.ngZone.runOutsideAngular(() => this.syncStickyBarsWidth()); }
  }
  // Instância do componente da barra (além do ElementRef acima) — pra disparar o feedback
  // visual do copy (flashCopied: ícone check + "Copiado!") no botão "Copiar" da barra.
  @ViewChild('actionBar') private actionBarComponent?: MenuSubmissionListComponent;
  @ViewChild('exportButtons', { read: ElementRef })
  set exportButtons(ref: ElementRef<HTMLElement> | undefined) {
    this.exportButtonsRef = ref || null;
    if (ref) { this.ngZone.runOutsideAngular(() => this.syncStickyBarsWidth()); }
  }
  private headerFrozen = false;
  private floatingRafId = 0;
  private pageHeaderEl: HTMLElement | null = null;
  // Loop de rAF: roda a sincronização a cada frame. Garante reação a QUALQUER
  // scroll (window, mat-sidenav-content, etc.) sem depender de detectar o scroller.
  private readonly floatingLoop = (): void => {
    this.syncFloatingHeader();
    this.floatingRafId = requestAnimationFrame(this.floatingLoop);
  };

  public onColumnsMenuClick(): void {
    this.columnsMenuToggle.emit(this.columnsMenuOrigin);
  }

  // Click no link do ID de uma submissão. Com Command (Mac) / Ctrl (Win/Linux)
  // pressionado, abre em nova aba no modo de visualização FULL (sem chrome do
  // app — só conteúdo da página). Sem o modificador, deixa o routerLink seguir
  // o comportamento padrão (navega in-place mantendo o layout vigente).
  public onSubmissionIdClick(ev: MouseEvent, submissionId: number): void {
    if (!ev.metaKey && !ev.ctrlKey) { return; }
    ev.preventDefault();
    ev.stopPropagation();
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        ['/admin', this.event.id, 'submissions', submissionId],
        // Em modo publicação, o ctrl+click preserva a visão travada (?view=publication).
        { queryParams: { viewMode: PageViewMode.Full, ...(this.isPublicationMode ? { view: 'publication' } : {}) } }
      )
    );
    window.open(url, '_blank');
  }

  // Sombra na barra de títulos (cabeçalho sticky) quando o card está rolado para
  // baixo (scrollTop > 0) — dá profundidade indicando conteúdo por baixo.
  public headerScrolled = false;
  // Há colunas roladas para fora à esquerda (scrollLeft > 0) → fade na borda esquerda.
  public scrolledLeft = false;
  // Há colunas escondidas à direita (não rolou até o fim) → fade na borda direita.
  public scrolledRight = false;
  // FAB "voltar ao topo" UNIFICADO: usamos o <app-scroll-top-fab> global (ver template).
  // Ele cuida de mostrar/esconder (limiar próprio) e da animação; aqui só apontamos o
  // container de scroll (setter cardEl) e suspendemos trabalho pesado durante a animação
  // (onFabScrollStart/End).
  @ViewChild(ScrollTopFabComponent) private scrollFab?: ScrollTopFabComponent;
  // Flag durante a animação programática de voltar-ao-topo: o (scroll) pula o
  // updateScrollFades (com getBoundingClientRect → reflow + CD na tabela toda),
  // que senão starvava os frames do rAF e fazia "pular" em vez de animar.
  private autoScrolling = false;
  // Tooltip dos pills de critério (coluna de score): elemento no <body> + listeners nativos
  // delegados. Feito assim porque matTooltip/title/CSS-:hover NÃO funcionam nesse cabeçalho
  // (overlay do fade z-index 101 + clone do cabeçalho flutuante). Nativo + body = imune a isso.
  private critTipEl?: HTMLElement;
  private critTipOver?: (e: MouseEvent) => void;
  private critTipOut?: (e: MouseEvent) => void;
  public onTableScroll(event: any): void {
    if (this.autoScrolling) { return; }
    this.updateScrollFades(event.target as HTMLElement);
  }

  // Início/fim da animação de voltar-ao-topo, disparados pelo <app-scroll-top-fab>.
  // Durante a animação DESANEXAMOS a CD deste componente e a flag `autoScrolling` faz o
  // `onTableScroll` pular o `updateScrollFades` (getBoundingClientRect → reflow) — senão
  // o (scroll) de cada frame re-checaria a tabela inteira e starvaria o rAF (jank na
  // tabela grande). No fim, reata + ressincroniza o estado final (sombra/fades).
  // `scrollStart` chega EM zona (via (click) do FAB); `scrollEnd` chega FORA (rAF), daí o ngZone.run.
  public onFabScrollStart(): void {
    this.autoScrolling = true;
    this.cdr.detach();
  }
  public onFabScrollEnd(): void {
    this.autoScrolling = false;
    this.ngZone.run(() => {
      this.cdr.reattach();
      const el = this.tableScrollRef?.nativeElement;
      if (el) { this.updateScrollFades(el); }
      this.cdr.detectChanges();
    });
  }

  // Tooltip dos pills de critério: cria um elemento no <body> e registra listeners NATIVOS
  // (mouseover/mouseout) delegados no host, FORA da zona Angular. Funciona no cabeçalho real E
  // no clone frozen (via `data-tip`, copiado no clone), sem sofrer com overlay/overflow/corte.
  private setupCritTooltip(): void {
    this.ngZone.runOutsideAngular(() => {
      const tip = document.createElement('div');
      tip.setAttribute('role', 'tooltip');
      // Aparência = classe global `.sg-crit-tooltip` (igual ao mat-tooltip, claro/escuro).
      // Inline só o essencial de posicionamento/estado.
      tip.className = 'sg-crit-tooltip';
      tip.style.cssText = 'position:fixed;z-index:3000;pointer-events:none;display:none;';
      document.body.appendChild(tip);
      this.critTipEl = tip;

      this.critTipOver = (e: MouseEvent): void => {
        const t = e.target as HTMLElement;
        const pill = t && t.closest ? (t.closest('.sg-crit-pill') as HTMLElement | null) : null;
        // Fora de um pill → esconde (senão o tooltip "gruda" ao sair do critério).
        if (!pill) { tip.style.display = 'none'; return; }
        const text = pill.getAttribute('data-tip') || '';
        if (!text) { tip.style.display = 'none'; return; }
        tip.textContent = text;
        tip.style.display = 'block';
        const r = pill.getBoundingClientRect();
        const tw = tip.offsetWidth;
        let left = r.left + r.width / 2 - tw / 2;
        left = Math.max(4, Math.min(left, window.innerWidth - tw - 4));
        tip.style.left = `${left}px`;
        tip.style.top = `${r.bottom + 6}px`;
      };
      this.critTipOut = (e: MouseEvent): void => {
        const t = e.target as HTMLElement;
        if (t && t.closest && t.closest('.sg-crit-pill')) { tip.style.display = 'none'; }
      };
      const host = this.hostRef.nativeElement;
      host.addEventListener('mouseover', this.critTipOver);
      host.addEventListener('mouseout', this.critTipOut);
    });
  }

  // Atualiza os estados de scroll (sombra do topo + fade esquerdo + fade direito) a partir
  // da geometria do card. Chamado no (scroll) — já na zona do Angular — e em mudanças de
  // tamanho/conteúdo via ResizeObserver (lá embrulhado em ngZone.run, pois altera bindings).
  private updateScrollFades(el: HTMLElement): void {
    const down = el.scrollTop > 0;
    if (down !== this.headerScrolled) { this.headerScrolled = down; }
    const left = el.scrollLeft > 0;
    if (left !== this.scrolledLeft) { this.scrolledLeft = left; }
    // Fade direito reflete colunas escondidas da TABELA: comparamos a borda direita da
    // <table> com a borda direita VISÍVEL do card. NÃO usamos `el.scrollWidth` porque a
    // barra de ações (mais larga que a área visível) infla o scrollWidth do card e
    // dispararia o fade mesmo com a tabela inteira visível.
    const table = el.querySelector('table.submission-table') as HTMLElement | null;
    const tableRect = table ? table.getBoundingClientRect() : null;
    let right = false;
    if (tableRect && tableRect.width > 0) {
      const visibleRight = el.getBoundingClientRect().left + el.clientWidth;
      right = tableRect.right > visibleRight + 1;
    }
    if (right !== this.scrolledRight) { this.scrolledRight = right; }
    this.cdr.markForCheck();   // OnPush: flags de scroll/fade (chamado via RO fora de evento do template)
  }

  // Busca controlada externamente (quando o campo vive no header do pai). Aplica
  // o filtro só depois que dados e forms já carregaram, para não rodar no bind inicial.
  @Input()
  public set searchTerm(value: string) {
    this.search = value || '';
    if (this.submissionRanking && this.formFilters) {
      this.applyFilter();
    }
  }

  public SubmissionStatus = SubmissionStatus;

  public event: Event;
  public eventReviewConfiguration: EventReviewConfiguration;
  public submissionRanking: SubmissionRanking[];

  public displayedColumns: string[] = ['position', 'id', 'status', 'description', 'group', 'event', 'track', 'select'];
  public fieldsShow: string[] = [];
  private fieldsShowOrdered: string[] = []; // This is necessary to know which column is the first and the last one.
  public dataSource: MatTableDataSource<SubmissionRanking>;

  public filteredDataSource: MatTableDataSource<SubmissionRanking>;

  public formFilters: FormGroup;
  public formFields: FormControl;
  
  public customFields: Array<{id: number, label: string, control: string, name: string}> = [];
  private customCheckListSubmissions: {} = {};
  public customControlForm: FormGroup;
  
  private previousFilter: {} = {};
  private tableSort: { sortBy: string, reverseSort: boolean };
  
  public submissionForm: Form;
  private reviewForm: Form;
  
  public paperGroupForm: FormGroup;
  public paperGroupList: Array<SelectOption>;

  public TPCGroupForm: FormGroup;
  public TPCGroupList: Array<SelectOption>;

  public statusForm: FormGroup;
  public status: Array<SelectOption> = SubmissionStatus.toSelectable();
  public iconCollection: { [key: string]: { fontSet: string, fontIcon: string, color: string } } = {};

  public readonly BUTTON_TYPE_ICONS = {CHECK: 'fa-check-square', UNCHECK: 'fa-square', INDETERMINATE: 'fa-minus-square'};
  private POSITION_PUBLICATION: number = null;

  private previousElementHovered: HTMLElement;

  private search : string = '';

  private colorScheme: number = ColorSchemeFilter['Submission status'];

  @ViewChild(MatSort, { static: false }) set content(sort: MatSort) {
    if (sort) {
      this.dataSource.sort = sort;

      if (!this.dataSource.sort.active) {
        this.initTableSorted();
      }

      this.dataSource.sortingDataAccessor = (item: SubmissionRanking, header: string) => {
        switch (header) {
          case 'id': return item.submission.id;
          case 'status': return item.submission.status;
          case 'description': return item.submission.title.toLowerCase();
          case 'group': return this.paperGroupList.find(group => group.id === item.submission.paperGroup)?.value || 'Ω';
          case 'tpc-group': return this.TPCGroupList.find(TPCGroup => TPCGroup.id === item.submission.tpcGroup)?.value || 'Ω';
          case 'track': return item.submission.trackName;
          case 'rebuttal': return item.submission.rebuttal;
          case 'discussion-messages': return item.submission.discussionMessages.length;
          case 'reviews': return item.submission.reviews.length;
          case 'score-average-arithmetic': return rankSort(item, 'average');
          case 'score-average-weighted': return rankSort(item, 'weightedAverage');
          case 'score-span': return rankSort(item, 'span');

          default: return item[header];
        }
      };
    }
    this.filteredDataSource = this.dataSource;
    const rankSort = (submission: SubmissionRanking, type: string) => {
      if (this.dataSource.sort.direction === 'asc') {

        if (Number.isNaN(submission.rank[type])) return 99;

        return submission.rank[type];
      }

      if (Number.isNaN(submission.rank[type])) return -1;

      return submission.rank[type];
    }
  }

  private countryPipe: CountryPipe;

  // Colorir as linhas com a cor do status (toggle do header). Compartilha a preferência
  // `statusColorAsBackgroundColor` com o toggle de Minhas submissões (persistida).
  public colorRowsByStatus = true;

  // Renderiza a legenda + barra de ações (elementos secundários) SÓ DEPOIS de a tabela pintar.
  // Ligado uma vez, num requestAnimationFrame após o primeiro render com dados (ver setTableSource).
  public tableRendered = false;

  constructor(
    private adminService: AdminService,
    private submissionsService: SubmissionsService,
    private fb: FormBuilder,
    private eventsService: EventsService,
    private reviewConfigurationService: ReviewsConfigurationService,
    private publicationService: PublicationService,
    private cdr: ChangeDetectorRef,
    private exportService: ExportService,
    private filesService: FilesService,
    private ngZone: NgZone,
    private translate: TranslateService,
    private preferencesService: PreferencesService,
    private notificationService: NotificationService,
    private router: Router,
    private hostRef: ElementRef<HTMLElement>,
  ) {
    // País por extenso no export HTML (mesma conversão do template: BRA → "Brasil").
    this.countryPipe = new CountryPipe(this.translate);
    // (A preferência de coloração das linhas é lida no ngOnInit, em initColorPreference(),
    // quando o @Input `page` já está vinculado — a chave depende do modo da página.)
    this.iconCollection = {
      'ACCEPTED': {...SubmissionStatus.icon(SubmissionStatus.ACCEPTED), color: '#99FF99'},
      'ACTIVE': {...SubmissionStatus.icon(SubmissionStatus.ACTIVE), color: '#FFFF55'},
      'REJECTED': {...SubmissionStatus.icon(SubmissionStatus.REJECTED), color: '#FFBDBD'},
      'PENDING': {...SubmissionStatus.icon(SubmissionStatus.PENDING), color: '#FAB65C'},
      'WITHDRAWN': {...SubmissionStatus.icon(SubmissionStatus.WITHDRAWN), color: '#DDDDDD'},
    }
  }

  ngOnInit(): void {
    this.initColorPreference();
    this.setupCritTooltip();
    setTimeout(() => {
      this.event = this.adminService.selectedEvent;

      if (!this.event) {
        this.getEvent();
        return;
      }

      this.getData();
      this.cdr.markForCheck();   // OnPush: `event` setado fora de evento do template
    });
  }

  // ANTES: `ngAfterContentChecked` chamava `this.cdr.detectChanges()` a cada ciclo — forçava
  // CD full toda hora E anulava o OnPush. Removido o método por completo (lint
  // `no-empty-lifecycle-method`); o render agora vem de markForCheck() nos pontos de mutação
  // assíncrona.

  // Cabeçalho fixo é via sticky do mat-table dentro do container (.table-card com
  // overflow + max-height) — não precisa do clone flutuante via JS. O setup dos
  // observers e o estado inicial dos fades vivem em `initCardSync` (chamado pelo setter
  // do card), porque o card nasce sob *ngIf de dados — aqui ele ainda não existe.

  // Disparado quando o card (#tableScroll) entra/sai do DOM (vive sob *ngIf de dados).
  // É o ponto certo (não o ngAfterViewInit) para configurar os observers e calcular o
  // estado inicial dos fades — em especial o fade DIREITO já visível se a tabela nasce
  // mais larga que a área visível.
  @ViewChild('tableScroll', { read: ElementRef })
  set cardEl(ref: ElementRef<HTMLElement> | undefined) {
    if (ref) {
      this.ngZone.runOutsideAngular(() => this.initCardSync(ref.nativeElement));
      // Aponta o FAB global para ESTE card (o scroll container). Aqui, e não via binding no
      // template, porque o card nasce/morre sob *ngIf de dados; o setter de scrollContainer
      // no FAB re-alveja o listener quando o elemento chega/troca.
      if (this.scrollFab) { this.scrollFab.scrollContainer = ref.nativeElement; }
    } else {
      this.cardResizeObserver?.disconnect();
      if (this.scrollFab) { this.scrollFab.scrollContainer = undefined; }
    }
  }

  private initCardSync(card: HTMLElement): void {
    this.syncStickyBarsWidth();
    const RO: any = (window as any).ResizeObserver;
    if (typeof RO === 'undefined') { return; }
    // Recria o observer a cada aparição do card (o elemento muda quando o *ngIf alterna).
    this.cardResizeObserver?.disconnect();
    this.cardResizeObserver = new RO(() => {
      // DEBOUNCE: no load, a tabela preenche async → o RO dispara VÁRIAS vezes seguidas. Sem
      // coalescer, cada disparo lia layout (getBoundingClientRect/clientWidth) da DOM de 194
      // linhas → N reflows. Aqui juntamos a rajada num ÚNICO rAF (cancela-e-reagenda), medindo/
      // escrevendo uma vez só por frame.
      if (this.roScheduled) { return; }
      this.roScheduled = true;
      requestAnimationFrame(() => {
        this.roScheduled = false;
        this.syncStickyBarsWidth();               // só ESCRITAS de estilo (larguras/alturas)
        // updateScrollFades LÊ getBoundingClientRect da tabela. Se rodasse AQUI, logo após as
        // escritas do syncStickyBarsWidth, a leitura forçaria um reflow da TABELA INTEIRA a cada
        // frame do resize (dobra o custo de layout). Adiando pro PRÓXIMO frame, as escritas já
        // foram aplicadas pelo browser → a leitura usa o layout limpo, sem reflow forçado.
        requestAnimationFrame(() => this.ngZone.run(() => this.updateScrollFades(card)));
      });
    });
    this.cardResizeObserver.observe(card);
    const table = card.querySelector('table.submission-table');
    if (table) { this.cardResizeObserver.observe(table); }
  }

  // Largura das barras sticky + do fade direito = largura visível do card (clientWidth). As
  // barras se alinham/centralizam nela; o fade direito a usa para sua borda direita coincidir
  // com a borda direita do card.
  private syncStickyBarsWidth(): void {
    const card = this.tableScrollRef?.nativeElement;
    if (!card) { return; }
    // Largura = CONTENT BOX do card. `clientWidth` INCLUI o padding lateral interno do card
    // (`padding: 0 var(--canvas-pad-x)`), então setar as barras/fades em clientWidth as deixava
    // ~16px MAIS LARGAS que a área útil → estouravam o content box e criavam scroll horizontal
    // (era o "lar" da barra flutuante extrapolando). Descontamos o padding pra elas caberem.
    // Largura = clientWidth do card. O card NÃO tem padding lateral (full-bleed; o recuo do
    // conteúdo vive na `.submission-table`, não no scroll container), então clientWidth = a área
    // útil inteira → barras e fade direito ancoram na BORDA do card, sem estourar o scrollWidth.
    // LÊ clientWidth E clientHeight JUNTOS, ANTES de qualquer escrita de estilo — senão a leitura
    // de clientHeight (após escrever as larguras) força um reflow ("Forced reflow" no console).
    const clientWidth = card.clientWidth;
    const clientHeight = card.clientHeight;
    const width = `${clientWidth}px`;
    // Altura dos fades = altura VISÍVEL do card (clientHeight), não a viewport. Com height fixa de
    // viewport no SCSS, os fades (sticky) ficavam mais altos que o card quando há poucas linhas →
    // scroll vertical FANTASMA. Seguindo o clientHeight, o fade encolhe junto. margin-bottom =
    // -altura cancela a contribuição no fluxo.
    const fadeHeight = `${clientHeight}px`;
    const fadeMarginBottom = `${-clientHeight}px`;

    // A partir daqui, SÓ escritas (nenhuma leitura de layout) → sem reflow.
    if (this.actionBarRef?.nativeElement) { this.actionBarRef.nativeElement.style.width = width; }
    if (this.exportButtonsRef?.nativeElement) { this.exportButtonsRef.nativeElement.style.width = width; }
    const legendBar = card.querySelector('.legend-bar') as HTMLElement | null;
    if (legendBar) { legendBar.style.width = width; }
    const rightFade = card.querySelector('.scroll-fade-right') as HTMLElement | null;
    if (rightFade) { rightFade.style.width = width; }
    (card.querySelectorAll('.scroll-fade-left, .scroll-fade-right') as NodeListOf<HTMLElement>)
      .forEach(fade => { fade.style.height = fadeHeight; fade.style.marginBottom = fadeMarginBottom; });
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.floatingRafId);
    this.cardResizeObserver?.disconnect();
    if (this.critTipOver) { this.hostRef.nativeElement.removeEventListener('mouseover', this.critTipOver); }
    if (this.critTipOut) { this.hostRef.nativeElement.removeEventListener('mouseout', this.critTipOut); }
    this.critTipEl?.remove();
    this.resetPublicationFields();
  }

  // Linha de congelamento = base do cabeçalho fixo da página (.main-header, agora
  // position: fixed → posição estável). O clone congela logo abaixo dele. Fallback:
  // base do breadcrumb (36px) caso o cabeçalho da página não seja encontrado.
  private computeFreezeTop(): number {
    if (!this.pageHeaderEl || !this.pageHeaderEl.isConnected) {
      this.pageHeaderEl = document.querySelector('.submission-ranking .main-header') as HTMLElement | null;
    }
    if (this.pageHeaderEl) {
      return this.pageHeaderEl.getBoundingClientRect().bottom;
    }
    const breadcrumb = document.querySelector('.event-breadcrumb') as HTMLElement | null;
    return breadcrumb ? breadcrumb.getBoundingClientRect().bottom : 36;
  }

  // Mostra/posiciona/sincroniza o cabeçalho flutuante conforme o scroll.
  private syncFloatingHeader(): void {
    const scrollEl = this.tableScrollRef?.nativeElement;
    const floatEl = this.floatingHeaderRef?.nativeElement;
    if (!scrollEl || !floatEl) { return; }
    const table = scrollEl.querySelector('table.submission-table') as HTMLElement | null;
    const thead = table ? table.querySelector('thead') as HTMLElement | null : null;
    // Tabela escondida (não ordenada) ou ausente → não congela.
    if (!table || !thead || table.offsetParent === null) {
      this.hideFloatingHeader(floatEl);
      return;
    }

    const freezeTop = this.computeFreezeTop();
    const tableRect = table.getBoundingClientRect();
    const theadHeight = thead.offsetHeight;
    const shouldFreeze = tableRect.top < freezeTop && tableRect.bottom > freezeTop + theadHeight;

    if (!shouldFreeze) {
      this.hideFloatingHeader(floatEl);
      return;
    }

    if (!this.headerFrozen) {
      this.renderHeaderClone(floatEl, table, thead);
      floatEl.style.display = 'block';
      this.headerFrozen = true;
    }

    const scrollRect = scrollEl.getBoundingClientRect();
    floatEl.style.top = `${freezeTop}px`;
    floatEl.style.left = `${scrollRect.left}px`;
    floatEl.style.width = `${scrollEl.clientWidth}px`;
    const inner = floatEl.firstElementChild as HTMLElement | null;
    if (inner) { inner.style.transform = `translateX(${-scrollEl.scrollLeft}px)`; }
  }

  private hideFloatingHeader(floatEl: HTMLElement): void {
    if (this.headerFrozen) {
      floatEl.style.display = 'none';
      this.headerFrozen = false;
    }
  }

  // Clona o cabeçalho real numa tabela própria, copiando as larguras das colunas
  // para alinhar com o corpo. Clone é display-only (sem bindings do Angular).
  private renderHeaderClone(floatEl: HTMLElement, table: HTMLElement, thead: HTMLElement): void {
    floatEl.innerHTML = '';
    const cloneTable = table.cloneNode(false) as HTMLElement;
    cloneTable.removeAttribute('id');
    cloneTable.removeAttribute('hidden');
    cloneTable.style.width = `${table.offsetWidth}px`;
    cloneTable.style.margin = '0';

    const cloneThead = thead.cloneNode(true) as HTMLElement;
    const origCells = Array.from(thead.querySelectorAll('th')) as HTMLElement[];
    const cloneCells = Array.from(cloneThead.querySelectorAll('th')) as HTMLElement[];
    origCells.forEach((cell, index) => {
      const clone = cloneCells[index];
      if (!clone) { return; }
      const width = `${cell.offsetWidth}px`;
      clone.style.width = width;
      clone.style.minWidth = width;
      clone.style.maxWidth = width;
    });

    cloneTable.appendChild(cloneThead);
    floatEl.appendChild(cloneTable);
  }

  private resetPublicationFields(): void {
    /*
      * This method is necessary to remove references the dynamic fields
    */
    if (this.POSITION_PUBLICATION) groupFields.splice(this.POSITION_PUBLICATION, 1);
    const indexesPublication = columnsOrder.map((field, index) => field.startsWith(publicationFields.customFields) ? index : null).filter(index => index);
    columnsOrder.splice(indexesPublication[0], indexesPublication.length);
  }

  private getEvent(): void {
    this.adminService.getEvent().subscribe({
      next: (event: Event) => { this.event = event; this.cdr.markForCheck(); },
      complete: () => this.getData()
    });
  }

  FilterNormalize(value: string): string {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  

  private applyFilter() : void {
    this.setTableSource();
    this.filterSubmissions();
    this.filteredDataSource = this.dataSource;
    const needle = this.FilterNormalize(this.search).toLowerCase();
    if (!needle) { return; }   // busca vazia = mantém tudo (como o includes('') anterior)
    this.filteredDataSource.data = this.filteredDataSource.data.filter(x =>
      this.buildSearchHaystack(x.submission).includes(needle)
    );
  }

  // String pesquisável da submissão p/ o filtro textual: título + nome dos autores +
  // e-mail dos autores + afiliação (acrônimo e nome). Normalizada (sem acentos) e minúscula.
  // Assim a busca casa também com autor, e-mail e afiliação, não só o título.
  private buildSearchHaystack(submission: any): string {
    const parts: string[] = [submission?.title || ''];
    (submission?.authors || []).forEach((author: any) => {
      const u = author?.user || {};
      parts.push(`${u.firstName || ''} ${u.lastName || ''}`, u.email || '');
      const aff = u.profile?.affiliation;
      if (aff) { parts.push(aff.acronym || '', aff.name || ''); }
    });
    return this.FilterNormalize(parts.join(' ')).toLowerCase();
  }

  // Labels dos critérios de score (event-wide), pro cabeçalho da coluna "Notas individuais"
  // que migrou pra linha de títulos principal. Mesma fonte do fallback de byReview.labels
  // (reviewForm.assignableFields), então casa com as colunas das sub-tabelas de cada célula.
  public scoreColumnLabels(): string[] {
    return this.reviewForm?.assignableFields?.map(f => f.shortLabel) || [];
  }

  // Cabeçalho compacto dos critérios: inicial (pill cinza) + descrição completa (tooltip).
  // Mesma fonte/ordem de scoreColumnLabels (reviewForm.assignableFields), então casa com as
  // colunas de valores nas células.
  public scoreColumnHeaders(): { initial: string, full: string }[] {
    return (this.reviewForm?.assignableFields || []).map(f => {
      const base = (f.shortLabel || f.description || '').trim();
      return {
        initial: base ? base.charAt(0).toUpperCase() : '?',
        // Tooltip = TÍTULO do critério (shortLabel), não o texto explicativo (description).
        full: (f.shortLabel || f.description || '').trim()
      };
    });
  }

  // ===== Metadados derivados PRÉ-COMPUTADOS (P0 perf) =====================================
  // Antes o template chamava métodos/getters DENTRO de *ngFor (scoreColumnLabels/Headers,
  // scoreGridTemplate, scoreSpanCols, scoreRanges, isSetField) → recriava arrays/strings e
  // fazia busca linear A CADA change detection, por célula. Agora são CAMPOS, recomputados só
  // quando `reviewForm`/`fieldsShow` mudam (recomputeDerived). O template lê o campo.
  public scoreLabels: string[] = [];
  public scoreHeaders: { initial: string, full: string }[] = [];
  public scoreRanges: string[] = [];     // faixa min–max por critério (tooltip das CÉLULAS de score)
  public scoreTooltips: string[] = [];   // legenda (título + "valor: descrição") p/ o CABEÇALHO da coluna
  public scoreSpanCount = 2;
  public scoreGridCols = '3rem repeat(0, 3.25rem) 3.75rem 3rem';
  // Flags de visibilidade de coluna (substituem isSetField(...) no template).
  public vis: { [k: string]: boolean } = {};
  // Rótulos i18n do cabeçalho da coluna do título, só dos sub-campos ligados (em ordem).
  public descriptionHeaderKeys: string[] = [];

  private recomputeDerived(): void {
    const fs = this.fieldsShow || [];
    // Em publication mode, as colunas do grupo "Revisões e notas" são forçadas
    // pra OFF independente do estado persistido — o seletor não as expõe mais,
    // então preferências antigas não devem "vazar" o eixo pra este contexto.
    const scoreGroupCols = new Set([
      'score-numeric', 'score-average-arithmetic', 'score-average-weighted',
      'score-span', 'reviews', 'rebuttal', 'discussion-messages', 'reviewer-name',
    ]);
    const has = (f: string) => {
      if (this.isPublicationMode && scoreGroupCols.has(f)) { return false; }
      return fs.includes(f);
    };
    const anyAuthor = has('authors-names') || has('authors-affiliations') || has('authors-countries') || has('authors-emails');
    this.vis = {
      id: has('id'), status: has('status'), description: has('description'),
      group: has('group'), 'tpc-group': has('tpc-group'), event: has('event'),
      track: has('track'), topics: has('topics'), abstract: has('abstract'),
      'score-numeric': has('score-numeric'),
      'score-average-arithmetic': has('score-average-arithmetic'),
      'score-average-weighted': has('score-average-weighted'),
      'score-span': has('score-span'), reviews: has('reviews'),
      rebuttal: has('rebuttal'), 'discussion-messages': has('discussion-messages'),
      'others-form': has('others-form'), registration: has('registration'),
      copyright: has('copyright'), files: has('files'), 'reviewer-name': has('reviewer-name'),
      // Ícone de similaridade no file-pill: sub-campo de "files" (preferência
      // do usuário) E só quando o evento tem a verificação habilitada —
      // mesmo se a preferência salva estiver ligada, some em tempo real
      // caso o chair desligue similarityVerification depois.
      'files-similarity': has('files-similarity') && !!this.eventReviewConfiguration?.similarityVerification,
      'authors-names': has('authors-names'), 'authors-emails': has('authors-emails'),
      'authors-affiliations': has('authors-affiliations'), 'authors-countries': has('authors-countries'),
      anyAuthor,
      namesOrEmails: has('authors-names') || has('authors-emails'),
      affOrCountry: has('authors-affiliations') || has('authors-countries'),
      // A coluna do título aparece se QUALQUER sub-campo dela (título, resumo, autores
      // ou tópicos) estiver ligado — não só o título.
      descriptionColumn: has('description') || has('abstract') || anyAuthor || has('topics'),
    };

    // Cabeçalho da coluna do título: só os sub-campos ligados, na ordem, sem "/" à toa.
    const descKeys: string[] = [];
    if (has('description')) descKeys.push('forms.fields.title');
    if (anyAuthor) descKeys.push('forms.fields.authors');
    if (has('topics')) descKeys.push('forms.fields.topics');
    if (has('abstract')) descKeys.push('forms.fields.abstract');
    this.descriptionHeaderKeys = descKeys;

    const fields = this.reviewForm?.assignableFields || [];
    this.scoreLabels = this.scoreColumnLabels();
    this.scoreHeaders = this.scoreColumnHeaders();
    // Faixa min–max por critério → tooltip das CÉLULAS de score (cada valor).
    this.scoreRanges = fields.map(f => {
      const vals = (f.choices || []).map(c => c.value).filter(v => v != null);
      let min: number, max: number;
      if (vals.length) { min = Math.min(...vals); max = Math.max(...vals); }
      else if (f.min != null && f.max != null) { min = f.min; max = f.max; }
      else { return ''; }
      return min === max ? `${min}` : `${min}–${max}`;
    });
    // Legenda completa por critério → tooltip do CABEÇALHO da coluna (pill). TÍTULO + a escala
    // inteira, uma linha por valor: "Título\n1: descrição\n…\nN: descrição" (sem separadores ; / .
    // no fim das linhas). Multi-linha via `white-space: pre-line` no balão `.sg-crit-tooltip`.
    this.scoreTooltips = fields.map(f => {
      const title = (f.shortLabel || f.description || '').trim();
      const choices = (f.choices || [])
        .filter(c => c.value != null)
        .slice()
        .sort((a, b) => a.value - b.value);
      if (!choices.length) { return title; }
      const lines = choices.map(c => `${c.value}: ${(c.description || '').trim()}`);
      return [title, ...lines].join('\n');
    });
    const n = this.scoreLabels.length;
    this.scoreSpanCount = n + 2;
    this.scoreGridCols = `3rem ${has('reviewer-name') ? '10rem ' : ''}repeat(${n}, 3.25rem) 3.75rem 3rem`;
  }

  // Separador decimal conforme o idioma atual: pt usa vírgula, o resto usa ponto. O LOCALE_ID
  // é estático, mas o idioma troca em runtime (ngx-translate), então formatamos pelo
  // currentLang. Recebe o valor já formatado (ex.: toFixed) e só troca o "." por ",".
  public locNum(value: any): string {
    if (value == null) { return ''; }
    const s = String(value);
    return (this.translate.currentLang || '').toLowerCase().startsWith('pt') ? s.replace('.', ',') : s;
  }

  // (scoreRanges / scoreGridTemplate / scoreSpanCols viraram campos pré-computados acima —
  // ver recomputeDerived; o template lê scoreRanges / scoreGridCols / scoreSpanCount.)

  // Ícone (fontSet/fontIcon) do status de uma revisão — mesma fonte de verdade dos cards de
  // revisão (ReviewStatus.icon). Usado no pill só-ícone da coluna Status do grid de scores.
  public getReviewStatusIcon(status: string): { fontSet: string, fontIcon: string } {
    return ReviewStatus.icon(status as ReviewStatus);
  }

  private getData(): void {
    this.getReviewFormAndSubmissions();
    this.getFieldsAndFilters();
    this.getReviewConfiguration();
    this.getFormSubmission();
    this.getSubmissionAnswer();
    this.getTPCGroups();
  }

  // Review form e submissões EM PARALELO (forkJoin), antes serial (review form → submissões).
  // O `/full/?detailed` (request mais caro, ~2s no servidor) passa a começar imediatamente, em
  // vez de esperar o review form terminar. O processamento (buildSubmissionRanking → rank()
  // pode clonar this.reviewForm) só roda quando AMBOS chegam, e setamos reviewForm ANTES de
  // construir o ranking → o rank() nunca vê reviewForm undefined (sem risco de regressão).
  private getReviewFormAndSubmissions(): void {
    this.adminService.progressBar.start();
    forkJoin({
      reviewForm: this.eventsService.getFormReview(this.event.id),
      submissions: this.submissionsService.getSubmissionsByEventFull(this.event.id, { detailed: true, reviews: false })
    }).subscribe(({ reviewForm, submissions }) => {
      this.reviewForm = reviewForm;
      this.recomputeDerived();   // reviewForm mudou → recomputa scoreLabels/Headers/Ranges/GridCols
      this.buildSubmissionRanking(submissions);
      this.initSubmissionGroupForm(submissions);
      this.initTPCGroupForm(submissions);
      this.getPaperGroups(this.event.id);
      this.initStatusForm(submissions);
      this.buildCustomControlForm(submissions);
      this.cdr.markForCheck();
    });
  }

  private getFieldsAndFilters(): void {
    this.eventsService.getSubmissionFieldsAndFilters(this.event.id, this.page).subscribe((response) => {
      this.initFieldsForm(response);
      this.initFiltersForm(response);
      this.handlePublicationsFields();
      this.tableSort = { sortBy: response.sortBy, reverseSort: response.reverseSort };
      this.cdr.markForCheck();
    });
  }

  private getReviewConfiguration(): void {
    this.reviewConfigurationService.getReviewsConfiguration(this.event.id)
      .subscribe(config => {
        this.eventReviewConfiguration = config;
        // `vis['files-similarity']` depende de similarityVerification — essa
        // config chega em paralelo com fieldsShow (ordem não garantida), então
        // recomputa aqui também pra não ficar presa no valor calculado antes
        // dela chegar.
        this.recomputeDerived();
        this.cdr.markForCheck();
      });
  }

  private getFormSubmission(): void {
    this.eventsService.getFormSubmission(this.event.id)
      .subscribe(form => { this.submissionForm = form; this.cdr.markForCheck(); });
  }

  private getSubmissionAnswer(): void {
    this.publicationService.getSubmissionsAnswers(this.event.id, {category: FormFieldCategory.CUSTOM})
      .subscribe((answers) => { this.customCheckListSubmissions = answers; this.cdr.markForCheck(); });
  }

  private getPaperGroups(eventID: number): void {
    this.eventsService.getEventPaperGroups(eventID)
      .subscribe({
        next: (groups) => { this.paperGroupList = groups.map(group => new SelectOption(group.id, group.name)); this.cdr.markForCheck(); },
        complete: () => this.filterSubmissions()
      });
  }

  private getTPCGroups(): void {
    this.eventsService.getEventTPCGroups(this.event.id, 'minimum')
      .subscribe(TPCgroups => {
        this.TPCGroupList = TPCgroups.map(TPCgroup => new SelectOption(TPCgroup.id, TPCgroup.name));
        this.cdr.markForCheck();
      })
  }

  // Monta todos os controles num mapa e cria o FormGroup DE UMA VEZ. `addControl` em
  // laço dispara updateValueAndValidity() a cada chamada (recalcula o grupo inteiro) →
  // O(N²); o construtor recalcula só uma vez no fim → O(N). Mesmo resultado, sem o custo.
  private initSubmissionGroupForm(submissions: Submission[]): void {
    const controls: { [key: string]: FormControl } = {};
    submissions.forEach(submission => {
      controls[`paperGroup-${submission.id}`] = new FormControl(submission.paperGroup);
    });
    this.paperGroupForm = new FormGroup(controls);
  }

  private initTPCGroupForm(submissions: Submission[]): void {
    const controls: { [key: string]: FormControl } = {};
    submissions.forEach(submission => {
      controls[`TPCGroup-${submission.id}`] = new FormControl(submission.tpcGroup);
    });
    this.TPCGroupForm = new FormGroup(controls);
  }

  private initFiltersForm(data: { [key: string] : any}): void {
    this.formFilters = this.fb.group({
      [SubmissionsFilter.STATUS]: [this.getFormFilterValue(SubmissionsFilter.STATUS, data)],
      [SubmissionsFilter.TOPICS]: [this.getFormFilterValue(SubmissionsFilter.TOPICS, data)],
      [SubmissionsFilter.TRACKS]: [this.getFormFilterValue(SubmissionsFilter.TRACKS, data)],
      [SubmissionsFilter.SUBMISSION_GROUPS]: [this.getFormFilterValue(SubmissionsFilter.SUBMISSION_GROUPS, data)],
      [SubmissionsFilter.TPC_GROUPS]: [this.getFormFilterValue(SubmissionsFilter.TPC_GROUPS, data)],
      [SubmissionsFilter.COLOR_SCHEME]: [this.page === 'ranking' ? ColorSchemeFilter['Submission status'] : ColorSchemeFilter['Publication status']],
    });
  }

  private initStatusForm(submissions: Submission[]): void {
    const controls: { [key: string]: FormControl } = {};
    submissions.forEach(submission => {
      controls[`status-${submission.id}`] = new FormControl(submission.status);
    });
    this.statusForm = new FormGroup(controls);
  }

  private initFieldsForm(controls: {}): void {
    let fieldsShow = [defaultFields.POSITION];
    Object.keys(controls).forEach((key) => {
      const value = controls[key];
      const control = dictControlFields[key];

      if (value && control) {
        if (this.isSpecialFieldsColumn(control)) {
          this.addSpecialColumnsField(control, fieldsShow);
        }

        fieldsShow.push(control);
      }

    });
    fieldsShow.push(defaultFields.SELECT);
    
    this.getChecklistFields(controls, fieldsShow);
  }

  private getChecklistFields(fields: {}, fieldShow: string[]): void {
    this.publicationService.getEventCheckListFields(this.event.id)
      .subscribe((customFields: CheckListField[]) => this.handleCustomFields(fields, customFields, fieldShow));
  }

  private handleCustomFields(fields: {}, customFields: CheckListField[], fieldsShow: string[]): void {
    // Check if there are any normal publication fields set (author reg and copyright)
    // And if there are custom fields
    if(customFields && !this.POSITION_PUBLICATION){
      const publication = {
        label: 'forms.fields.filters.publication',
        fields: [],
        control: 'publication'
      };

      groupFields.push(publication);
      this.POSITION_PUBLICATION = groupFields.length - 1;
    }
    customFields.forEach(field => {
      const customField = { 
        id: field.id,
        label: field.description, 
        control: `${publicationFields.customFields}-${field.id}`, 
        name: `${publicationFields.customFields}-${field.id}` 
      };

      this.customFields.push(customField);
      groupFields[this.POSITION_PUBLICATION].fields.push(customField);
      dictControlFields[customField.control] = customField.control;
      columnsOrder.splice(columnsOrder.indexOf(defaultFields.SELECT), 0, customField.control);
    });

    this.setFieldsValues(fields, fieldsShow);
  }

  private setFieldsValues(fields: {}, fieldsShow: string[]): void {
    // O CSV de custom fields pode vir null/undefined (ex.: evento sem custom fields de
    // publicação configurados). Sem a guarda, `null.split` lançava e ABORTAVA todo o
    // setFieldsValues → formFields/fieldsShow/displayedColumns nunca eram setados, deixando
    // a lista com colunas em branco e o seletor de colunas vazio.
    const customFieldsSaved = (fields[publicationFields.customFields] || '').split(',').filter(id => isValidNumber(id));
    fieldsShow = fieldsShow.concat(customFieldsSaved.map(id => `${publicationFields.customFields}-${id}`));

    this.formFields = new FormControl(fieldsShow);
    this.fieldsShow = shallowCopyArray(fieldsShow);
    this.recomputeDerived();   // fieldsShow mudou → recomputa flags de visibilidade + scoreGridCols
    this.fieldsShowOrdered = intersectionArrays(columnsOrder, fieldsShow);
    this.displayedColumns = this.removeSpecialFields(fieldsShow);
    this.cdr.markForCheck();   // OnPush: colunas iniciais/custom mudaram (chamado via subscribe)
  }

  private removeSpecialFields(fields: string[]): string[] {
    const fieldsColumn = fields.filter(field => !specialFields.includes(field));
    // A coluna do título (matColumnDef="description") também hospeda resumo, autores
    // (nome/afiliação/país/e-mail) e tópicos — todos specialFields, sem coluna própria.
    // Se algum deles estiver ligado, a coluna precisa existir mesmo com o título OFF
    // (senão não há onde renderizá-los).
    const descSubfields = [
      dictControlFields['abstract'], dictControlFields['name'], dictControlFields['affiliation'],
      dictControlFields['country'], dictControlFields['email'], dictControlFields['topics'],
    ];
    if (!fieldsColumn.includes('description') && fields.some(f => descSubfields.includes(f))) {
      fieldsColumn.push('description');
    }
    return this.orderFields(fieldsColumn);
  }

  private getFormFilterValue(type: string, data: {}): number[] | string[] {
    const key = Object.keys(dictControlFilters).find(k => dictControlFilters[k] === type);

    if (type === SubmissionsFilter.STATUS) {
      return !!data[key] ? data[key]?.split(',') : [];
    }

    return !!data[key] ? data[key]?.split(',').map((id: string) => Number(id)) : [];
  }

  private handlePublicationsFields(): void {
    const { authorRegistrationEnable, copyrightEnable } = this.event;

    if (authorRegistrationEnable || copyrightEnable) {
      const publication = {
        label: 'forms.fields.filters.publication',
        fields: [],
        control: 'publication'
      };
      
      groupFields.push(publication);
      this.POSITION_PUBLICATION = groupFields.length - 1;
    }

    if (authorRegistrationEnable) {
      groupFields[this.POSITION_PUBLICATION].fields.push(publicationFields['registration']);
    }

    if (copyrightEnable) {
      groupFields[this.POSITION_PUBLICATION].fields.push(publicationFields['copyright']);
    }
  }

  public showSubmissions(): void {
    setTimeout(() => {
      this.filterSubmissions();
      this.fieldsShow = shallowCopyArray(this.formFields.value);
      this.recomputeDerived();   // fieldsShow mudou → recomputa flags + scoreGridCols
      this.fieldsShowOrdered = intersectionArrays(columnsOrder, this.fieldsShow);
      this.checkNewColumns();
      this.adminService.progressBar.stop();
      this.cdr.markForCheck();   // OnPush: colunas/campos visíveis mudaram
    });
  }

  public filterSubmissions(): void {
    const onFilters = this.formFilters.value;
    const prevScheme = this.colorScheme;
    this.colorScheme = onFilters.colorScheme;
    // Esquema de cor mudou (status ↔ publicação) → recomputa a classe de cor de cada linha.
    if (prevScheme !== this.colorScheme) { this.recomputeColorClasses(); }

    if (isEqual(this.previousFilter, onFilters)) return;
    
    this.previousFilter = onFilters;
    this.setTableSource(this.filteredSubmissions);
  }

  private checkNewColumns(): void {
    const newFields = this.formFields.value.filter(field => {
      if (specialFields.includes(field)) return false;
      return !this.displayedColumns.includes(field);
    });
    
    if (arrayHasElements(newFields)) {
      this.displayedColumns = this.displayedColumns.concat(newFields);
      this.displayedColumns = this.removeSpecialFields(this.displayedColumns);
      this.displayedColumns = this.orderFields(this.displayedColumns);
    }
  }

  private get filteredSubmissions(): SubmissionRanking[] {
    let submissions = this.submissionRanking;
    // getRawValue (NÃO .value): os selects de filtro desabilitados quando não têm
    // opções (ex.: evento sem grupos de submissão) são EXCLUÍDOS do `formGroup.value`
    // pelo Angular → o campo viria `undefined` e o `.filter` abaixo estourava (barra de
    // atividade travada, lista não atualizava). getRawValue inclui os desabilitados
    // com seu valor `[]` (= sem filtro).
    let { status, topics, tracks, submissionGroups, tpcGroups } = this.formFilters.getRawValue();

    submissionGroups = submissionGroups.filter(x => 
      this.paperGroupList.some(group => group.id === x)
    );

    if (arrayHasElements(status)) {
      submissions = submissions.filter(({submission}) => status.includes(submission.status));
    }

    if (arrayHasElements(topics)) {
      submissions = submissions.filter(({submission}) => submission.topics.some(topic => topics.includes(topic.id)));
    }

    if (arrayHasElements(tracks)) {
      submissions = submissions.filter(({submission}) => tracks.includes(submission.trackID));
    }

    if (arrayHasElements(submissionGroups)) {
      submissions = submissions.filter(({submission}) => submissionGroups.includes(submission.paperGroup));
    }

    if (arrayHasElements(tpcGroups)) {
      submissions = submissions.filter(({submission}) => tpcGroups.includes(submission.tpcGroup));
    }

    return submissions;
  }

  private setTableSource(submissions: SubmissionRanking[] = this.submissionRanking): void {
    if (this.dataSource) {
      // Atualiza os DADOS da MESMA instância (não recria). Recriar zerava o `sort` (o
      // @ViewChild(MatSort) só conecta uma vez, na init) → tableIsSorted() ficava false
      // → a tabela ([hidden]="!tableIsSorted()") ficava EM BRANCO ao aplicar filtros,
      // só voltando ao recarregar a página. Atualizando `.data`, o sort/sortingDataAccessor
      // e o re-render seguem funcionando.
      this.dataSource.data = submissions;
    } else {
      this.dataSource = new MatTableDataSource(submissions);
    }
    // A tabela binda `filteredDataSource`; mantém apontando pra instância atual.
    this.filteredDataSource = this.dataSource;
    if (this.dataSource.sort?.active || !arrayHasElements(this.dataSource.data)) this.adminService.progressBar.stop();
    this.cdr.markForCheck();   // OnPush: render central da tabela (dados/filtro mudaram)
  }

  private buildSubmissionRanking(submissions: Submission[]): void {
    this.submissionRanking = submissions.map((submission) => ({
      submission,
      rank: this.rank(submission),
      selected: false,
      colorClass: this.getColorSchemeClass(submission)
    }));
  }

  // Recomputa a classe de cor de TODAS as linhas (barato, roda só em toggle de esquema/status).
  private recomputeColorClasses(): void {
    (this.submissionRanking || []).forEach(item => item.colorClass = this.getColorSchemeClass(item.submission));
  }

  // trackBy dos *ngFor das colunas Arquivos e Itens-do-formulário: os métodos que os alimentam
  // (getTrackFiles / getSubmissionFormAnswers) retornam array NOVO a cada CD → sem trackBy o
  // Angular destruía/recriava o DOM dessas células em TODAS as linhas por CD (inclusive no
  // toggle). Com trackBy ele reusa o DOM.
  public trackByTrackFileId(_i: number, tf: any): any { return tf?.id ?? _i; }
  public trackByAnswerOrder(_i: number, a: any): any { return a?.order ?? _i; }

  private buildCustomControlForm(submissions: Submission[]): void {
    // O(N × custom fields): construímos o mapa inteiro e criamos o FormGroup de uma vez
    // (ver nota em initSubmissionGroupForm) — antes era addControl em laço aninhado,
    // recalculando o grupo a cada um → custo quadrático no nº total de controles.
    const keys = Object.keys(this.customCheckListSubmissions);
    const controls: { [key: string]: FormControl } = {};
    submissions.forEach(submission => {
      keys.forEach(key => {
        controls[`${key}-${submission.id}`] = new FormControl(this.customCheckListSubmissions[key].includes(submission.id));
      });
    });
    this.customControlForm = new FormGroup(controls);
  }

  private initTableSorted(): void {
    setTimeout(() => {
      this.dataSource.sort.active = this.tableSort.sortBy || 'id';
      this.dataSource.sort.direction = this.tableSort.reverseSort ? 'desc' : 'asc';
      this.dataSource.sort.sortChange.emit();
      this.adminService.progressBar.stop();
      this.cdr.markForCheck();   // OnPush: ordenação aplicada

      // SÓ AGORA a tabela fica visível (tableIsSorted() = true, sai do [hidden]) e renderiza. Só
      // então liga o flag que revela a legenda + barra de ações (elementos SECUNDÁRIOS) — no frame
      // seguinte, depois de a tabela pintar. Fazer isso no setTableSource ligava cedo demais (antes
      // de a ordenação aplicar e a tabela sair do [hidden]). O 2º rAF sincroniza a largura das
      // barras sticky depois que entram no DOM (o card tem altura fixa → o ResizeObserver não
      // dispara sozinho quando o conteúdo interno cresce).
      if (!this.tableRendered) {
        requestAnimationFrame(() => {
          this.tableRendered = true;
          this.cdr.markForCheck();
          requestAnimationFrame(() => this.syncStickyBarsWidth());
        });
      }
    });
  }

  public submissionsDeleted(submissionIDs: number[]): void {
    this.dataSource.data = this.dataSource.data.filter(({submission}) => !submissionIDs.includes(submission.id));
  }

  public updatePaperGroupList(options:Array<SelectOption>) {
    this.paperGroupList = options;
    this.setTableSource(this.filteredSubmissions)
  }

  public updateTPCGroupList(options:Array<SelectOption>) {
    this.TPCGroupList = options;
  }

  public updateStatusForm(): void {
    this.statusForm.reset();
    this.initStatusForm(this.submissionRanking.map(({submission}) => submission));
  }

  public changeRowColor($event): void {
    const item = this.dataSource.data.find(it => it.submission.id === $event.submissionID);
    if (item) {
      item.submission.status = $event.value;
      item.colorClass = this.getColorSchemeClass(item.submission);   // status mudou → recolore a linha
    }
    this.statusForm.get(`status-${$event.submissionID}`).setValue($event.value);
    this.cdr.markForCheck();   // OnPush
  }
  
  public isSetField(field: string[]): boolean {
    let isSet = false;
    for (const f of field) {
      if (this.fieldsShow.includes(f)) {
        isSet = true;
        break;
      }
    }

    return isSet;
  }

  // Visibilidade de coluna para o EXPORT — espelha a tela: a coluna do título
  // (`description`) aparece se QUALQUER sub-campo dela (título/resumo/autores/tópicos)
  // estiver ligado, não só o título. Demais colunas seguem o `isSetField` normal.
  private exportColumnVisible(col: string): boolean {
    return col === 'description' ? !!this.vis['descriptionColumn'] : this.isSetField([col]);
  }

  private isSpecialFieldsColumn(control: string): boolean {
    return Object.keys(specialColumnsFields).includes(control);
  }

  private addSpecialColumnsField(control: string, formsFieldsValue: string[]): void {
    Object.keys(specialColumnsFields).forEach(key => {
      if (key === control) {
        formsFieldsValue.push(...specialColumnsFields[key]);
      }
    });
  }

  public toggleSelection(submission: SubmissionRanking): void {
    submission.selected = !submission.selected;
  }

  // Checkbox "selecionar/desmarcar tudo" no cabeçalho da coluna de seleção.
  // Opera sobre as linhas visíveis (dataSource.data), igual ao toggle por linha.
  public get allRowsSelected(): boolean {
    const rows = this.dataSource?.data ?? [];
    return rows.length > 0 && rows.every(row => row.selected);
  }

  // Estado indeterminado do checkbox do cabeçalho: algumas (mas não todas) marcadas.
  public get someRowsSelected(): boolean {
    const rows = this.dataSource?.data ?? [];
    return rows.some(row => row.selected) && !this.allRowsSelected;
  }

  public get selectAllIcon(): string {
    const rows = this.dataSource?.data ?? [];
    const selectedCount = rows.filter(row => row.selected).length;

    if (selectedCount === 0) return this.BUTTON_TYPE_ICONS.UNCHECK;
    if (selectedCount === rows.length) return this.BUTTON_TYPE_ICONS.CHECK;
    return this.BUTTON_TYPE_ICONS.INDETERMINATE;
  }

  public toggleSelectAll(): void {
    const rows = this.dataSource?.data ?? [];
    const select = !this.allRowsSelected;

    rows.forEach(row => row.selected = select);
  }

  // Seletores "interativos": clicar em (ou dentro de) um deles tem comportamento
  // próprio e NÃO deve limpar a seleção (checkboxes, botões, links, o menu de status,
  // a barra de ações, menus em overlay, ordenação de coluna, etc.).
  private readonly interactiveSelector =
    'button, a, input, textarea, select, label,' +
    ' mat-checkbox, .mat-checkbox, mat-select, .mat-select, app-status-select,' +
    ' app-menu-submission-list, .mat-menu-item, [matmenutriggerfor],' +
    ' .mat-sort-header, .cdk-overlay-container, .mat-menu-panel,' +
    ' [role="button"], [role="menuitem"], [role="checkbox"]';

  // Havendo linha(s) selecionada(s), um clique em qualquer elemento NÃO clicável
  // desmarca todas as seleções da tabela (UX "clicar fora para desmarcar").
  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    const rows = this.dataSource?.data ?? [];
    if (!rows.some(row => row.selected)) { return; }            // nada selecionado
    const target = event.target as HTMLElement | null;
    if (target?.closest(this.interactiveSelector)) { return; }  // clicou em algo clicável
    rows.forEach(row => row.selected = false);                  // desmarca tudo
  }

  public getIconToggleAll(): string {
    const selectionFirstSubmission = this.submissionRanking[0].selected;
    const hasDifferentSelection = this.submissionRanking.find(submission => submission.selected !== selectionFirstSubmission);

    if (hasDifferentSelection) return this.BUTTON_TYPE_ICONS.INDETERMINATE;
    return selectionFirstSubmission ? this.BUTTON_TYPE_ICONS.CHECK : this.BUTTON_TYPE_ICONS.UNCHECK;
  }

  private addClassToTableRows($class: string, select?: boolean, selector?: HTMLElement): void {
    setTimeout(() => {
      if (selector) {
        selector.classList.toggle($class);
        return;
      }
  
      const rows = document.querySelectorAll('.submission-table tr');
      for (const row of rows) {
        if (select) {
          row.classList.add($class);
          continue;
        }
        row.classList.remove($class);
      }
    });
  }

  // Coloração das linhas, INDEPENDENTE por modo:
  //  - ranking: preferência do backend `statusColorAsBackgroundColor` (compartilhada com
  //    Minhas submissões — mesmo conceito de "cor por status de submissão").
  //  - publication: chave PRÓPRIA em localStorage. O PreferencesService só persiste um
  //    conjunto FIXO de chaves no perfil e reconstrói o cache do perfil a cada escrita,
  //    descartando chaves extras — então publication usa storage próprio (frontend-only),
  //    nunca interferindo no estado do ranking.
  private static readonly PUBLICATION_COLOR_KEY = 'publicationColorAsBackgroundColor';

  // Lê a preferência de coloração do modo atual (chamado no ngOnInit, quando `page` já
  // está vinculado). Default colorido quando não definida; respeita o false explícito.
  private initColorPreference(): void {
    if (this.page === 'publication') {
      const stored = localStorage.getItem(SubmissionsListTableV2Component.PUBLICATION_COLOR_KEY);
      this.colorRowsByStatus = stored === null ? true : stored === 'true';
    } else {
      const colorPref = this.preferencesService.getPreference('statusColorAsBackgroundColor');
      this.colorRowsByStatus = colorPref === undefined || colorPref === null ? true : !!colorPref;
    }
  }

  // Liga/desliga a coloração e PERSISTE no armazenamento do modo (independente entre
  // ranking e publication), de forma silenciosa.
  public setColorRowsByStatus(checked: boolean): void {
    this.colorRowsByStatus = checked;
    // OnPush + evento no PAI (submission-ranking chama isto via template ref `rankingTable`):
    // o clique marca a view do PAI como dirty, mas NÃO este filho. Sem markForCheck, o
    // `[ngClass]` das 194 linhas só re-renderiza no próximo tick (por isso "só funciona quando
    // mexo o mouse sobre a tabela"). Marca o filho → o tick disparado pelo evento o re-checa.
    this.cdr.markForCheck();
    if (this.page === 'publication') {
      localStorage.setItem(SubmissionsListTableV2Component.PUBLICATION_COLOR_KEY, String(checked));
    } else {
      this.preferencesService.changePreference('statusColorAsBackgroundColor', checked);
      this.preferencesService.saveCachedPreferences().subscribe();
    }
  }

  public getColorSchemeClass(submission: Submission): string {
    if (this.colorScheme === ColorSchemeFilter['Publication status']) {
      const { authorRegistrationEnable, copyrightEnable } = this.event;

      if (authorRegistrationEnable && !submission.authorRegistration) return 'not-ready';

      if (copyrightEnable && !submission.copyrightIEEE) return 'not-ready';

      if (!this.isPublicationReady()[submission.id]?.includes(false)) return 'ready'

      return 'not-ready';
    }

    return submission.status.toLowerCase();
  }

  private isPublicationReady(): {} {
    const publicationFieldsValue = this.customControlForm.value;
    const submissionPublicationValues = {};

    Object.entries(publicationFieldsValue).forEach(([key, value]) => {
      const [field, submissionID] = key.split('-');

      if (!submissionPublicationValues[submissionID]) {
        submissionPublicationValues[submissionID] = [value];
      } else {
        submissionPublicationValues[submissionID].push(value);
      }
    });

    return submissionPublicationValues;
  }

  public getPublicationColumnsClass(submission: Submission, field: { control: string, id?: string }, value: boolean = undefined): string {
    if (this.colorScheme === ColorSchemeFilter['Publication status']) {
      if (value != undefined) {
        const className = value ? 'publication-ok' : 'publication-missing';
        return `${this.addBorderColumnClass(field.control)} ${className}`;
      }
  
      const className = this.customControlForm.get(`${field.id}-${submission.id}`).value ? 'publication-ok' : 'publication-missing';
      return `${this.addBorderColumnClass(field.control)} ${className}`;
    }

    return this.addBorderColumnClass(field.control);
  }

  public showRankValue(value: number): string {
    if (Number.isNaN(value)) return '-';

    const text = value.toFixed(2);
    // Separador decimal por idioma: vírgula em português, ponto nos demais.
    return (this.translate.currentLang || '').toLowerCase().startsWith('pt') ? text.replace('.', ',') : text;
  }

  public subtitleValues(status: SubmissionStatus): number {
    return this.dataSource.data.filter(({submission}) => submission.status === status).length;
  }
  
  public conflictSubtitleValues(conflict: 'span-under-A' | 'span-above-B' | 'span-between-A-and-B'): number {
    return this.dataSource.data.filter(item => {
      switch (conflict) {
        case 'span-under-A':
          return item.rank.span < this.eventReviewConfiguration.spanLimitA;
        case 'span-above-B':
          return item.rank.span > this.eventReviewConfiguration.spanLimitB;
        case 'span-between-A-and-B':
          return item.rank.span >= this.eventReviewConfiguration.spanLimitA && item.rank.span <= this.eventReviewConfiguration.spanLimitB;
      }
    }).length;
  }

  public getControlStatus(status: boolean) : string {
    return `admin.event.publication.status.${status ? 'checked' : 'unchecked'}`;
  }

  // ============================================================
  // Exportação da tabela VISÍVEL para EXCEL (.xlsx / OOXML)
  // ============================================================
  // Gera um .xlsx REAL (OOXML, empacotado com JSZip em `utils/xlsx-export.ts`) — abre
  // no Excel SEM o aviso de "formato/extensão não conferem". A ÚNICA formatação é a
  // COR DE FUNDO das linhas (fill): sem bordas, sem fonte custom, cabeçalho sem estilo.
  // Os "triggers" (status/grupo) viram texto; os arquivos viram hyperlink do Excel
  // para a URL do arquivo no JEMS3. Colunas = as visíveis (`displayedColumns`), exceto
  // a de seleção e a coluna "#" (position).

  // Cor de fundo por classe de status (mesmo retorno de getColorSchemeClass —
  // submissão OU publicação, conforme o color scheme ativo). Valores de _colors.scss.
  private static readonly EXPORT_ROW_COLORS: { [key: string]: string } = {
    active: '#FFFF55', pending: '#FAB65C', accepted: '#99FF99', rejected: '#FFBDBD',
    withdrawn: '#DDDDDD', deleted: '#DDDDDD', ready: '#6DA36F', 'not-ready': '#F93826',
  };

  // Cor das linhas quando o toggle "Colorir linhas com status" está DESLIGADO: o mesmo
  // cinza dos cards de submissão de "Minhas revisões" ($gray-125 = #F1F0EF).
  private static readonly EXPORT_NO_STATUS_COLOR = '#F1F0EF';

  // Chave de tradução do cabeçalho por coluna.
  private static readonly EXPORT_HEADER_KEYS: { [key: string]: string } = {
    id: 'forms.fields.id', status: 'forms.fields.status', description: 'forms.fields.title',
    authors: 'forms.fields.authors', abstract: 'forms.fields.abstract', topics: 'forms.fields.topics',
    group: 'forms.fields.submission-group', 'tpc-group': 'forms.fields.tpc-group',
    event: 'forms.fields.event', track: 'forms.fields.track',
    'score-numeric': 'forms.fields.filters.numeric-review-scores',
    rebuttal: 'submissions.rebuttal.title', 'discussion-messages': 'submissions.discussion-messages.title',
    reviews: 'forms.fields.reviews', 'score-average-arithmetic': 'forms.fields.score-average-arithmetic',
    'score-average-weighted': 'forms.fields.score-average-weighted', 'score-span': 'forms.fields.score-span',
    files: 'forms.fields.files', 'others-form': 'forms.fields.filters.submission-form-items',
    registration: 'admin.event.publication.status.registration', copyright: 'admin.event.publication.status.copyright',
  };

  // Colunas com conteúdo centralizado na HORIZONTAL na exportação HTML.
  // Colunas com conteúdo CENTRALIZADO nos downloads (HTML e Excel usam ESTE conjunto
  // único, pra não divergirem). As de texto longo ficam à esquerda: description e
  // others-form (e os custom de publicação são centralizados via startsWith no código).
  private static readonly EXPORT_CENTERED_COLS = new Set<string>([
    'position', 'id', 'status', 'group', 'tpc-group', 'event', 'track', 'rebuttal',
    'discussion-messages', 'reviews', 'score-average-arithmetic', 'score-average-weighted',
    'score-span', 'files', 'registration', 'copyright',
  ]);

  // Cor do ÍCONE do chip de status (tom escuro harmonizado, = nth(3) de $submission-status).
  private static readonly EXPORT_STATUS_ICON_COLORS: { [key: string]: string } = {
    active: '#1f3a93', pending: '#874d00', accepted: '#1c7530', rejected: '#b3261e',
    withdrawn: '#808080', deleted: '#808080',
  };

  // Cor de fundo do PILL de status de REVISÃO na mini-tabela (= 1º valor de $review-status,
  // _colors.scss). O ícone da tela é de fonte (jems3) → não embute em HTML standalone; no export
  // o pill leva a COR + o TEXTO do status (legível num doc estático, sem tooltip).
  // Cores EXATAS do pill de status de revisão = `lighten(cor,20%)` (bg) e `darken(cor,40%)` (borda +
  // ícone) do SCSS (_score-grid.scss `.sg-status` sobre $review-status), calculadas em HSL — as
  // MESMAS que a tela renderiza (não aproximação RGB). `bg` = fundo, `fg` = borda + ícone.
  private static readonly EXPORT_REVIEW_STATUS_PILL: { [key: string]: { bg: string; fg: string } } = {
    assigned: { bg: '#FFFFFF', fg: '#999999' }, notified: { bg: '#F6FAFF', fg: '#0754BC' },
    confirmed: { bg: '#FFF1C6', fg: '#936F00' }, declined: { bg: '#F9F9F9', fg: '#606060' },
    reminded: { bg: '#FFF5F7', fg: '#C2001D' }, late: { bg: '#FDBEBE', fg: '#850404' },
    delegated: { bg: '#FFEFF8', fg: '#BC0066' }, completed: { bg: '#C7FEEB', fg: '#039060' },
    draft: { bg: '#DDDDDD', fg: '#444444' },
  };

  // Ícone de cada status de revisão como SVG INLINE (path EXATO extraído dos glifos das fontes
  // jems3s/jems3r com fontTools; MESMO ícone da tela). Inline via <symbol>/<use> → sobrevive a
  // e-mail (fonte embutida vira "tofu" quando o cliente remove @font-face). `vb` = viewBox (bbox
  // do glifo, Y já invertido p/ SVG), `d` = path. A cor (fill) é a `fg` do EXPORT_REVIEW_STATUS_PILL.
  private static readonly EXPORT_REVIEW_STATUS_SVG: { [k: string]: { vb: string; d: string } } = {
    assigned: { vb: '0 192 1024 768', d: 'M1005 446Q1010 441 1017.0 444.5Q1024 448 1024 455V864Q1024 904 996.0 932.0Q968 960 928 960H96Q56 960 28.0 932.0Q0 904 0 864V455Q0 448 6.5 444.5Q13 441 19 446Q53 472 114.0 517.0Q175 562 328 673Q359 696 409.0 732.5Q459 769 512 768Q566 769 616.0 732.0Q666 695 697 673Q850 562 910.5 517.0Q971 472 1005 446ZM512 704Q547 705 590.5 672.5Q634 640 659 621Q858 477 909.0 438.5Q960 400 1006 364Q1014 357 1019.0 347.0Q1024 337 1024 326V288Q1024 248 996.0 220.0Q968 192 928 192H96Q56 192 28.0 220.0Q0 248 0 288V326Q0 337 5.0 347.0Q10 357 18 364Q64 400 115.0 438.5Q166 477 365 621Q390 639 433.5 672.0Q477 705 512 704Z' },
    notified: { vb: '0 192 1024 768', d: 'M1005 446Q1010 441 1017.0 444.5Q1024 448 1024 455V864Q1024 904 996.0 932.0Q968 960 928 960H96Q56 960 28.0 932.0Q0 904 0 864V455Q0 448 6.5 444.5Q13 441 19 446Q53 472 114.0 517.0Q175 562 328 673Q359 696 409.0 732.5Q459 769 512 768Q566 769 616.0 732.0Q666 695 697 673Q850 562 910.5 517.0Q971 472 1005 446ZM512 704Q547 705 590.5 672.5Q634 640 659 621Q858 477 909.0 438.5Q960 400 1006 364Q1014 357 1019.0 347.0Q1024 337 1024 326V288Q1024 248 996.0 220.0Q968 192 928 192H96Q56 192 28.0 220.0Q0 248 0 288V326Q0 337 5.0 347.0Q10 357 18 364Q64 400 115.0 438.5Q166 477 365 621Q390 639 433.5 672.0Q477 705 512 704Z' },
    confirmed: { vb: '0 64 1024 1024', d: 'M208 512H48Q28 512 14.0 526.0Q0 540 0 560V1040Q0 1060 14.0 1074.0Q28 1088 48 1088H208Q228 1088 242.0 1074.0Q256 1060 256 1040V560Q256 540 242.0 526.0Q228 512 208 512ZM128 1008Q108 1008 94.0 994.0Q80 980 80 960Q80 940 94.0 926.0Q108 912 128 912Q148 912 162.0 926.0Q176 940 176 960Q176 980 162.0 994.0Q148 1008 128 1008ZM768 227Q768 291 740.0 332.5Q712 374 701 416H905Q955 416 989.5 451.5Q1024 487 1024 532Q1024 559 1013.5 586.0Q1003 613 985 631Q1000 666 996.5 710.5Q993 755 966 790Q979 829 968.5 871.5Q958 914 934 939Q940 966 937.0 988.5Q934 1011 921 1029Q891 1073 824.5 1080.5Q758 1088 694 1088H688Q616 1088 556.0 1067.0Q496 1046 449 1025Q425 1014 396.0 1003.5Q367 993 343 992Q334 992 327.0 985.0Q320 978 320 968V541Q320 536 322.0 531.5Q324 527 327 524Q386 465 421.0 405.5Q456 346 505 297Q527 275 537.5 243.5Q548 212 556 180Q563 152 578.5 108.0Q594 64 624 64Q660 64 714.0 90.5Q768 117 768 227Z' },
    declined: { vb: '0 64 1024 1024', d: 'M0 176V656Q0 676 14.0 690.0Q28 704 48 704H208Q228 704 242.0 690.0Q256 676 256 656V176Q256 156 242.0 142.0Q228 128 208 128H48Q28 128 14.0 142.0Q0 156 0 176ZM80 576Q80 556 94.0 542.0Q108 528 128 528Q148 528 162.0 542.0Q176 556 176 576Q176 596 162.0 610.0Q148 624 128 624Q108 624 94.0 610.0Q80 596 80 576ZM624 1088Q594 1088 578.5 1044.0Q563 1000 556 972Q548 940 538.0 908.5Q528 877 505 855Q457 806 422.0 746.5Q387 687 327 628Q324 625 322.0 620.5Q320 616 320 611V184Q320 174 327.0 167.0Q334 160 344 160Q367 159 396.0 148.5Q425 138 449 127Q497 106 556.5 85.0Q616 64 688 64H694Q758 64 824.5 71.5Q891 79 921 123Q934 142 937.0 164.0Q940 186 933 213Q958 238 968.5 280.5Q979 323 966 362Q992 397 996.0 441.5Q1000 486 985 521Q1003 539 1013.5 566.0Q1024 593 1024 620Q1024 665 989.5 700.5Q955 736 905 736H701Q712 778 740.0 819.5Q768 861 768 925Q768 1035 714.0 1061.5Q660 1088 624 1088Z' },
    completed: { vb: '0 194 1024 764', d: 'M348 943 15 610Q0 595 0.0 574.0Q0 553 15 538L87 465Q102 450 123.5 450.0Q145 450 160 465L384 689L864 209Q879 194 900.5 194.0Q922 194 937 209L1009 282Q1024 297 1024.0 318.0Q1024 339 1009 354L420 943Q405 958 384.0 958.0Q363 958 348 943Z' },
    reminded: { vb: '32 64 320 1024', d: 'M352 928Q352 994 305.0 1041.0Q258 1088 192 1088Q126 1088 79.0 1041.0Q32 994 32 928Q32 862 79.0 815.0Q126 768 192 768Q258 768 305.0 815.0Q352 862 352 928ZM51 114 78 658Q79 678 92.5 691.0Q106 704 126 704H258Q277 704 291.0 691.0Q305 678 306 658L333 114Q334 94 320.0 79.0Q306 64 286 64H98Q78 64 64.0 79.0Q50 94 51 114Z' },
    late: { vb: '16 80 992 992', d: 'M512 80Q409 80 319 119Q229 158 161.5 225.5Q94 293 55 383Q16 473 16 576Q16 679 55 769Q94 859 161.5 926.5Q229 994 319 1033Q409 1072 512 1072Q615 1072 705 1033Q795 994 862.5 926.5Q930 859 969 769Q1008 679 1008 576Q1008 473 969 383Q930 293 862.5 225.5Q795 158 705 119Q615 80 512 80ZM512 976Q429 976 356 945Q283 913 229.0 859.0Q175 805 143 732Q112 659 112 576Q112 493 143 420Q175 347 229.0 293.0Q283 239 356 207Q429 176 512 176Q595 176 668 207Q741 239 795.0 293.0Q849 347 881 420Q912 493 912 576Q912 659 881 732Q849 805 795.0 859.0Q741 913 668 945Q595 976 512 976ZM636 767 466 644Q461 640 458.5 635.0Q456 630 456 624V296Q456 286 463.0 279.0Q470 272 480 272H544Q554 272 561.0 279.0Q568 286 568 296V579L702 677Q710 682 711.5 692.0Q713 702 707 710L669 762Q663 770 653.5 771.5Q644 773 636 767Z' },
    draft: { vb: '0 64 1024 1024', d: 'M581 250 837 506 281 1062 53 1088Q30 1090 14.0 1074.0Q-2 1058 0 1035L26 806L581 250ZM996 212 876 92Q847 64 807.5 64.0Q768 64 740 92L627 205L883 461L996 348Q1024 320 1024.0 280.0Q1024 240 996 212Z' },
    delegated: { vb: '0 64 1280 1024', d: 'M448 576Q501 576 548 556Q594 536 629.0 501.0Q664 466 684 420Q704 373 704 320Q704 267 684 220Q664 174 629.0 139.0Q594 104 548 84Q501 64 448 64Q395 64 348 84Q302 104 267.0 139.0Q232 174 212 220Q192 267 192 320Q192 373 212 420Q232 466 267.0 501.0Q302 536 348 556Q395 576 448 576ZM627 640H594Q561 655 524.0 663.5Q487 672 448 672Q409 672 372.5 663.5Q336 655 302 640H269Q213 640 164 661Q115 682 78.5 718.5Q42 755 21 804Q0 853 0 909V992Q0 1032 28.0 1060.0Q56 1088 96 1088H646Q642 1078 640.5 1067.0Q639 1056 641 1045L654 924L657 901L827 731Q790 689 738.5 664.5Q687 640 627 640ZM718 931 704 1053Q703 1068 713.5 1079.0Q724 1090 739 1088L861 1074L1137 798L994 655L718 931ZM1266 602 1190 526Q1176 512 1156.5 512.0Q1137 512 1123 526L1039 610L1182 753L1266 670Q1280 655 1280.0 635.5Q1280 616 1266 602Z' },
    deleted: { vb: '16 432 992 288', d: 'M656 576Q656 636 614.0 678.0Q572 720 512 720Q452 720 410.0 678.0Q368 636 368 576Q368 516 410.0 474.0Q452 432 512 432Q572 432 614.0 474.0Q656 516 656 576ZM864 432Q804 432 762.0 474.0Q720 516 720 576Q720 636 762.0 678.0Q804 720 864 720Q924 720 966.0 678.0Q1008 636 1008 576Q1008 516 966.0 474.0Q924 432 864 432ZM160 432Q100 432 58.0 474.0Q16 516 16 576Q16 636 58.0 678.0Q100 720 160 720Q220 720 262.0 678.0Q304 636 304 576Q304 516 262.0 474.0Q220 432 160 432Z' },
  };

  // Cores do pill da coluna Span por faixa (espelha .span-under-A / .span-above-B /
  // .span-between-A-and-B do scss: $span-below-A #75D775, $span-above-B #ff0000 com
  // texto #ffeeee, $span-between-A-and-B rgb(240,240,74) = #f0f04a). Peso 400.
  private static readonly EXPORT_SPAN_COLORS: { [key: string]: { bg: string; color?: string } } = {
    'span-under-A': { bg: '#75D775' },
    'span-above-B': { bg: '#ff0000', color: '#ffeeee' },
    'span-between-A-and-B': { bg: '#f0f04a' },
  };

  // Colunas de score (decimais) → campo do rank, p/ extrair o valor CRU no Excel
  // (célula número + formato "0.00", em vez do texto com vírgula do showRankValue).
  private static readonly EXPORT_RANK_FIELD: { [col: string]: string } = {
    'score-average-arithmetic': 'average',
    'score-average-weighted': 'weightedAverage',
    'score-span': 'span',
  };

  // Ícones de status (Material Symbols, FILL 1) extraídos da fonte self-hosted —
  // espelham os ícones da TELA. viewBox próprio por glifo (upem 960, Y já invertido p/ SVG).
  private static readonly EXPORT_STATUS_ICONS: { [key: string]: { vb: string; d: string } } = {
    active: { vb: '0 -960 960 960', d: 'M389 -267 195 -460 246 -512 389 -369 714 -693 765 -642Z' },
    accepted: { vb: '0 -960 960 960', d: 'M720 -144H264V-624L552 -912L584 -890Q602 -877 610.5 -857.0Q619 -837 614 -816L576 -624H840Q870 -624 891.0 -603.0Q912 -582 912 -552V-495Q912 -487 910.5 -480.5Q909 -474 906 -467L787 -188Q778 -168 760.0 -156.0Q742 -144 720 -144ZM336 -216H720Q720 -216 720.0 -216.0Q720 -216 720 -216L840 -495V-552Q840 -552 840.0 -552.0Q840 -552 840 -552H488L537 -795L336 -594ZM336 -594 537 -795 488 -552H840Q840 -552 840.0 -552.0Q840 -552 840 -552V-495L720 -216Q720 -216 720.0 -216.0Q720 -216 720 -216H336ZM192 -624V-552V-216V-144H48V-624Z' },
    rejected: { vb: '0 -960 960 960', d: 'M240 -816H696V-336L408 -48L376 -70Q358 -83 349.5 -103.0Q341 -123 346 -144L384 -336H120Q90 -336 69.0 -357.0Q48 -378 48 -408V-465Q48 -473 49.5 -479.5Q51 -486 54 -493L173 -772Q181 -792 199.5 -804.0Q218 -816 240 -816ZM624 -744H240Q240 -744 240.0 -744.0Q240 -744 240 -744L120 -465V-408Q120 -408 120.0 -408.0Q120 -408 120 -408H472L423 -165L624 -366ZM624 -366 423 -165 472 -408H120Q120 -408 120.0 -408.0Q120 -408 120 -408V-465L240 -744Q240 -744 240.0 -744.0Q240 -744 240 -744H624ZM768 -336V-408V-744V-816H912V-336Z' },
    pending: { vb: '0 -960 960 960', d: 'M480 -144Q450 -144 429.0 -165.0Q408 -186 408 -216Q408 -246 429.0 -267.0Q450 -288 480 -288Q510 -288 531.0 -267.0Q552 -246 552 -216Q552 -186 531.0 -165.0Q510 -144 480 -144ZM408 -384V-816H552V-384Z' },
    withdrawn: { vb: '0 -960 960 960', d: 'M216 -96Q186 -96 165.0 -117.0Q144 -138 144 -168V-580Q123 -588 109.5 -606.5Q96 -625 96 -648V-792Q96 -822 117.0 -843.0Q138 -864 168 -864H792Q822 -864 843.0 -843.0Q864 -822 864 -792V-648Q864 -625 850.5 -606.5Q837 -588 816 -580V-168Q816 -138 795.0 -117.0Q774 -96 744 -96ZM216 -576V-168Q216 -168 216.0 -168.0Q216 -168 216 -168H744Q744 -168 744.0 -168.0Q744 -168 744 -168V-576ZM168 -648H792Q792 -648 792.0 -648.0Q792 -648 792 -648V-792Q792 -792 792.0 -792.0Q792 -792 792 -792H168Q168 -792 168.0 -792.0Q168 -792 168 -792V-648Q168 -648 168.0 -648.0Q168 -648 168 -648ZM384 -408H576V-480H384ZM216 -576H744V-168Q744 -168 744.0 -168.0Q744 -168 744 -168H216Q216 -168 216.0 -168.0Q216 -168 216 -168Z' },
    deleted: { vb: '0 -960 960 960', d: 'M312 -144Q282 -144 261.0 -165.0Q240 -186 240 -216V-696H192V-768H384V-816H576V-768H768V-696H720V-216Q720 -186 699.0 -165.0Q678 -144 648 -144ZM648 -696H312V-216Q312 -216 312.0 -216.0Q312 -216 312 -216H648Q648 -216 648.0 -216.0Q648 -216 648 -216ZM384 -288H456V-624H384ZM504 -288H576V-624H504ZM312 -696H648V-216Q648 -216 648.0 -216.0Q648 -216 648 -216H312Q312 -216 312.0 -216.0Q312 -216 312 -216Z' },
  };

  // Linhas na ORDEM EXIBIDA pelo mat-table: o `dataSource.data` é filtrado mas NÃO
  // ordenado (o MatTableDataSource aplica o sort só no render). Aplicamos o mesmo sort
  // ativo (sortData usa o sortingDataAccessor configurado) pra o download bater com a
  // tela. Sem sort ativo → ordem natural do data.
  private getDisplayedRows(): SubmissionRanking[] {
    const ds = this.dataSource;
    if (!ds) { return []; }
    const data = ds.data ?? [];
    return (ds.sort && ds.sort.active && ds.sort.direction) ? ds.sortData(data, ds.sort) : data;
  }

  public async exportTableExcel(): Promise<void> {
    const scoreLabels = this.scoreColumnLabels();          // critérios (mesma ordem de review.answers)
    const hasScore = this.isSetField(['score-numeric']);   // coluna de notas visível?
    // Colunas visíveis, SEM a de seleção. `description` é EXPANDIDA em Título + (Autores/Resumo/
    // Tópicos). A coluna de NOTAS (`score-numeric`) — que na tela é uma mini-tabela 2D — é expandida
    // em colunas REAIS de revisão (Status | Revisor | critérios | Média | Peso); e cada submissão
    // vira UMA LINHA POR REVISOR (formato longo, com células numéricas processáveis). A "#" é mantida.
    const cols: string[] = [];
    (this.displayedColumns || []).filter(col => col !== 'select' && (col === 'position' || this.exportColumnVisible(col))).forEach(col => {
      if (col === 'score-numeric') {
        cols.push('rev-status', 'rev-reviewer');
        scoreLabels.forEach((_, j) => cols.push('rev-crit-' + j));
        cols.push('rev-average', 'rev-weight');
        return;
      }
      if (col === 'description') {
        // Título vira coluna SÓ se o campo título estiver ligado; resumo/autores/tópicos
        // são colunas próprias conforme seus campos. Assim, com o título OFF mas outros
        // ON, as colunas deles ainda saem.
        if (this.isSetField(['description'])) { cols.push('description'); }
        if (this.isSetField(['authors-names', 'authors-affiliations', 'authors-countries', 'authors-emails'])) { cols.push('authors'); }
        if (this.isSetField(['abstract'])) { cols.push('abstract'); }
        if (this.isSetField(['topics'])) { cols.push('topics'); }
        return;
      }
      cols.push(col);
    });
    const data = this.getDisplayedRows();
    const colors = SubmissionsListTableV2Component.EXPORT_ROW_COLORS;
    const RANK = SubmissionsListTableV2Component.EXPORT_RANK_FIELD;
    const isRevNum = (c: string) => c.startsWith('rev-crit-') || c === 'rev-average' || c === 'rev-weight';
    const r2 = (v: any) => Math.round(Number(v) * 100) / 100;   // ≤ 2 casas decimais no valor gravado
    const colL = (i: number): string => { let s = '', n = i + 1; while (n > 0) { s = String.fromCharCode(65 + (n - 1) % 26) + s; n = Math.floor((n - 1) / 26); } return s; };

    const headers = cols.map(col => {
      if (col === 'rev-status') { return this.translate.instant('forms.fields.status'); }
      if (col === 'rev-reviewer') { return this.translate.instant('forms.fields.reviewer'); }
      if (col.startsWith('rev-crit-')) { return scoreLabels[+col.slice('rev-crit-'.length)] || ''; }
      if (col === 'rev-average') { return this.translate.instant('forms.fields.score-average-arithmetic'); }
      if (col === 'rev-weight') { return this.translate.instant('forms.fields.weight'); }
      return this.exportHeader(col);
    });

    // Valor de uma coluna de revisão para a revisão `r` (null = linha de submissão sem revisão).
    const revValue = (col: string, r: any): string => {
      if (!r) { return ''; }
      if (col === 'rev-status') { return r.status ? this.translate.instant('status.' + r.status) : ''; }
      if (col === 'rev-reviewer') { return r.reviewer?.name || ''; }
      if (col.startsWith('rev-crit-')) { const v = r.answers?.[+col.slice('rev-crit-'.length)]; return v == null ? '' : String(r2(v)); }
      if (col === 'rev-average') { return (r.average == null || Number.isNaN(r.average)) ? '' : String(r2(r.average)); }
      if (col === 'rev-weight') { return (r.weight == null || Number.isNaN(r.weight)) ? '' : String(r2(r.weight)); }
      return '';
    };

    // EXPANSÃO: cada submissão vira UMA LINHA POR REVISÃO. As colunas da submissão (título, autores,
    // médias gerais, etc.) só aparecem na 1ª linha da submissão; nas linhas de continuação ficam EM
    // BRANCO (só as colunas de revisão são preenchidas). Sem revisões (ou sem a coluna de notas) →
    // uma linha só. A cor de fundo (status) repete em TODAS as linhas da submissão, pra agrupá-las.
    const rows: XlsxRow[] = [];
    const merges: string[] = [];   // ranges de células mescladas (colunas da submissão por bloco)
    data.forEach((item, index) => {
      const hex = this.colorRowsByStatus
        ? colors[this.getColorSchemeClass(item.submission)]
        : SubmissionsListTableV2Component.EXPORT_NO_STATUS_COLOR;
      const fillArgb = hex ? 'FF' + hex.replace('#', '').toUpperCase() : undefined;
      const reviews: any[] = (item.rank as any)?.byReview?.reviews || [];
      const reviewList: any[] = hasScore ? (reviews.length ? reviews : [null]) : [null];
      const firstRevRow = rows.length + 2;   // linha do Excel da 1ª revisão desta submissão (linha 1 = cabeçalho)
      reviewList.forEach((review, ri) => {
        const links: { [colIndex: number]: string } = {};
        const values = cols.map((col, colIndex) => {
          if (col.startsWith('rev-')) { return revValue(col, review); }
          if (ri > 0) { return ''; }   // linha de continuação: colunas da submissão em branco
          if (col === 'files') {
            const { text, href } = this.exportFiles(item);
            if (href) { links[colIndex] = href; }
            return text;
          }
          // Decimais da submissão (Avg./WAvg./Span): valor CRU → célula número + formato "0.00".
          const rf = RANK[col];
          if (rf) {
            const raw = (item.rank as any)?.[rf];
            return (raw == null || Number.isNaN(raw)) ? '-' : Number(raw).toFixed(2);
          }
          return this.exportCellText(col, item, index);
        });
        rows.push({ values, fillArgb, links: Object.keys(links).length ? links : undefined });
      });
      // Linha de MÉDIAS (rodapé "Avg."): FÓRMULAS do Excel que refazem os MESMOS cálculos do sistema
      // sobre as linhas de revisão acima (que vão de `firstRevRow` até `lastRevRow`):
      //  • critério  = AVERAGE da coluna do critério   (= getReviewFieldAverage: soma/contagem das completas)
      //  • Média     = AVERAGE da coluna Média          (= média das médias das revisões)
      //  • Peso      = SUMPRODUCT(Média,Peso)/SUM(Peso) (= weightedAverage; SUM(Peso)=0 → média simples)
      // Tudo em ROUND(...,2) (≤ 2 casas) e IFERROR("") quando não há completas. Só quando há revisões.
      if (hasScore && reviews.length) {
        const lastRevRow = rows.length + 1;
        const rng = (ci: number) => `${colL(ci)}${firstRevRow}:${colL(ci)}${lastRevRow}`;
        const aIdx = cols.indexOf('rev-average'), wIdx = cols.indexOf('rev-weight');
        const aRng = rng(aIdx), wRng = rng(wIdx);
        const formulas: { [colIndex: number]: string } = {};
        cols.forEach((col, ci) => {
          if (col.startsWith('rev-crit-')) { formulas[ci] = `IFERROR(ROUND(AVERAGE(${rng(ci)}),2),"")`; }
          else if (col === 'rev-average') { formulas[ci] = `IFERROR(ROUND(AVERAGE(${aRng}),2),"")`; }
          else if (col === 'rev-weight') { formulas[ci] = `IFERROR(IF(SUM(${wRng})=0,ROUND(AVERAGE(${aRng}),2),ROUND(SUMPRODUCT(${aRng},${wRng})/SUM(${wRng}),2)),"")`; }
        });
        const values = cols.map(col => col === 'rev-reviewer' ? this.translate.instant('forms.fields.score-average-arithmetic') : '');
        rows.push({ values, fillArgb, formulas });
      }
      // MESCLAGEM: cada coluna da SUBMISSÃO (não-revisão) do topo do bloco até a última linha da
      // submissão (revisões + linha de médias) vira UMA célula só — em vez de valor no topo +
      // vazios abaixo. Só quando o bloco tem mais de uma linha.
      const blockLastRow = rows.length + 1;
      if (blockLastRow > firstRevRow) {
        cols.forEach((col, ci) => { if (!col.startsWith('rev-')) { merges.push(`${colL(ci)}${firstRevRow}:${colL(ci)}${blockLastRow}`); } });
      }
    });

    // Colunas com quebra de texto (wrapText): Autores, Arquivos e Itens do formulário
    // (um item por linha) + Título, Resumo e Tópicos (texto longo quebrando na largura).
    const WRAP = new Set<string>(['authors', 'files', 'others-form', 'description', 'abstract', 'topics']);
    const wrapCols = cols.map((c, i) => WRAP.has(c) ? i : -1).filter(i => i >= 0);

    // Larguras de coluna (unidade Excel ≈ caracteres). Colunas com texto longo ficam
    // largas pra não quebrar demais — assim a altura da linha bate com o conteúdo
    // visível (sem sobra expressiva em cima/embaixo). Default p/ colunas não mapeadas.
    const W: { [k: string]: number } = {
      position: 5, id: 8, status: 11, description: 32, authors: 30, abstract: 50, topics: 22,
      group: 18, 'tpc-group': 18, event: 12, track: 14, rebuttal: 11, reviews: 9,
      'discussion-messages': 11, 'score-average-arithmetic': 8, 'score-average-weighted': 8,
      'score-span': 8, files: 22, 'others-form': 44, registration: 14, copyright: 16,
      'rev-status': 12, 'rev-reviewer': 24, 'rev-average': 8, 'rev-weight': 8,
    };
    const colWidths = cols.map(c => c.startsWith('rev-crit-') ? 7 : (W[c] ?? (c.startsWith('customFields-') ? 14 : 16)));

    // Colunas com conteúdo CENTRALIZADO horizontalmente (pedido): ID, Status, grupos,
    // evento, trilha, rebuttal, revisões, médias/span, arquivos e o controle de
    // publicação (registro, transferência/copyright e campos custom de publicação).
    // Mesmo conjunto único do HTML (EXPORT_CENTERED_COLS) — HTML e Excel centralizam as
    // MESMAS colunas, sem divergir. customFields-* (controle de publicação) via startsWith.
    // Centralizadas: as do HTML + o Status e as numéricas de CADA revisão (o Revisor fica à
    // esquerda, como texto).
    const CENTER = SubmissionsListTableV2Component.EXPORT_CENTERED_COLS;
    const centerCols = cols.map((c, i) => (CENTER.has(c) || c.startsWith('customFields-') || c === 'rev-status' || isRevNum(c)) ? i : -1).filter(i => i >= 0);

    // Coluna "#" (position): fundo SEM cor (não recebe a cor da linha).
    const noFillCols = cols.map((c, i) => c === 'position' ? i : -1).filter(i => i >= 0);

    // NUMÉRICAS: "#" + médias/span da submissão + critérios/média/peso de CADA revisão. As decimais
    // (médias e peso) recebem "0.00" (numFmtId 2); os critérios ficam no formato geral (int/decimal
    // conforme o valor). O Excel mostra a vírgula pelo locale.
    const numCols = cols.map((c, i) => (c === 'position' || RANK[c] || isRevNum(c)) ? i : -1).filter(i => i >= 0);
    const numFmtCols: { [col: number]: number } = {};
    cols.forEach((c, i) => { if (RANK[c] || c === 'rev-average' || c === 'rev-weight') { numFmtCols[i] = 2; } });

    const sheetName = this.translate.instant('admin.event.submissions.submissions-list.title') || 'Submissoes';
    const blob = await buildXlsxBlob(sheetName, headers, rows, wrapCols, colWidths, centerCols, noFillCols, numCols, numFmtCols, merges);
    const ev: any = this.event;
    this.exportService.downloadBlob(`${ev?.acronym || 'submissoes'}-tabela.xlsx`, blob);
  }

  // ============================================================
  // Exportação da tabela VISÍVEL para PÁGINA HTML (.html).
  // ============================================================
  // Monta a tabela — CABEÇALHO + CÉLULAS — a partir dos DADOS e embute um CSS MÍNIMO
  // para uma visualização COERENTE com o navegador: cor das linhas (status), bordas
  // leves, descrição combinada (título + autores + resumo + tópicos), arquivos como
  // links, colunas curtas centralizadas. Inclui a coluna "#" (posição); exclui a
  // coluna de seleção.
  public exportTableHtml(): void {
    const doc = this.buildExportHtmlDoc(this.getDisplayedRows(), { heading: true, legend: true });
    const ev: any = this.event;
    this.exportService.downloadFile(`${ev?.acronym || 'submissoes'}-tabela.html`, doc, 'text/html;charset=utf-8');
  }

  // Gera o HTML standalone (MESMAS definições do download) para um conjunto `rows`.
  // Reutilizado pelo download (heading=h1 + legend=tfoot) e pelo CTRL+C/área de
  // transferência (heading=false/legend=false — só a tabela das linhas selecionadas).
  // O índice da coluna "#" é o do ranking COMPLETO (getDisplayedRows), não a posição
  // dentro do subconjunto copiado.
  private buildExportHtmlDoc(rows: SubmissionRanking[], opts: { heading: boolean; legend: boolean; inline?: boolean; includePosition?: boolean }): string {
    // Colunas VISÍVEIS no momento (isSetField = fieldsShow, igual ao [hidden] da
    // tabela). `displayedColumns` é só o acumulado de matColumnDef, então filtra-se
    // por isSetField; a coluna "#" (position) é mantida por padrão, mas pode ser
    // excluída (includePosition:false) — usado no copiar p/ clipboard.
    const cols = (this.displayedColumns || []).filter(col => {
      if (col === 'select') { return false; }
      if (col === 'position') { return opts.includePosition !== false; }
      return this.exportColumnVisible(col);
    });
    // Índice (posição no ranking completo) por linha — p/ a coluna "#" ficar correta
    // mesmo ao copiar apenas um subconjunto de linhas selecionadas.
    const rankIndex = new Map<SubmissionRanking, number>();
    this.getDisplayedRows().forEach((r, i) => rankIndex.set(r, i));
    const colors = SubmissionsListTableV2Component.EXPORT_ROW_COLORS;
    const icons = SubmissionsListTableV2Component.EXPORT_STATUS_ICONS;
    const iconColors = SubmissionsListTableV2Component.EXPORT_STATUS_ICON_COLORS;
    const centered = SubmissionsListTableV2Component.EXPORT_CENTERED_COLS;
    const showStatus = cols.indexOf('status') >= 0;

    // Conjuntos PRESENTES nos dados — pra emitir só o CSS/symbols USADOS (em vez de
    // repetir cor/ícone inline em cada célula). É o que mais encolhe o HTML em tabelas
    // grandes: cor de linha (bg-/st-) por classe e ícone de status como <symbol>.
    const colorClassSet = new Set<string>();
    const statusSet = new Set<string>();
    rows.forEach(item => {
      const cc = this.getColorSchemeClass(item.submission);
      if (colors[cc]) { colorClassSet.add(cc); }
      if (showStatus) {
        const st = String(item.submission.status).toLowerCase();
        if (colors[st] || icons[st]) { statusSet.add(st); }
      }
    });

    const head = cols.map(col => {
      // Coluna de scores: o CABEÇALHO é o sub-grid (Status | Revisor | critérios | Avg | Peso),
      // UMA vez no topo (títulos das colunas de revisão não se repetem mais em cada linha).
      if (col === 'score-numeric') { return `<th>${this.exportScoreHeaderHtml()}</th>`; }
      // O cabeçalho da coluna de descrição é VARIÁVEL (como no navegador): Título +
      // (Autores / Tópicos / Resumo) conforme os sub-campos visíveis.
      const label = col === 'description' ? this.exportDescriptionHeader() : this.exportHeader(col);
      return `<th>${this.escapeHtml(label)}</th>`;
    }).join('');

    const body = rows.map(item => {
      const index = rankIndex.get(item) ?? 0;
      const colorClass = this.getColorSchemeClass(item.submission);
      // Cor de fundo da linha por CLASSE (não inline): respeita o toggle — ligado =
      // bg-<status>; desligado = bg-g (cinza). A faixa accent (st-<status>) usa a cor do
      // status SEMPRE, só na coluna ID. As regras .bg-/.st- vêm do CSS dinâmico abaixo.
      const bgClass = this.colorRowsByStatus ? (colors[colorClass] ? 'bg-' + colorClass : '') : 'bg-g';
      const stripeClass = colors[colorClass] ? 'st-' + colorClass : '';
      const cells = cols.map(col => {
        let inner: string;
        if (col === 'files') { inner = this.exportFilesHtmlLinks(item); }
        else if (col === 'description') { inner = this.exportDescriptionHtml(item); }
        else if (col === 'status') { inner = this.exportStatusChipHtml(item); }
        else if (col === 'score-span') { inner = this.exportSpanPillHtml(item); }
        else if (col === 'score-numeric') { inner = this.exportScoreHtml(item); }
        else if (col === 'others-form') { inner = this.exportFormItemsHtml(item); }
        // ID: SEM link (o link foi pro título) → cai no fallback genérico = "#id" texto puro.
        else { inner = this.escapeHtml(this.exportCellText(col, item, index)).replace(/\n/g, '<br>'); }
        const classes: string[] = [];
        if (centered.has(col) || col.startsWith('customFields-')) { classes.push('c'); }
        if (col === 'position') { classes.push('num'); }           // # = tipografia do cabeçalho
        if (col === 'description') { classes.push('desc'); }       // alinhamento vertical no topo
        // others-form NÃO recebe 'desc': fica no vertical-align:middle padrão do td.
        if (col === 'id') {
          classes.push('round-l');                                 // ID = início (arredondado) da pílula
          if (stripeClass) { classes.push(stripeClass); }          // + faixa accent (gradient via classe)
        }
        // Coluna "#" = TRANSPARENTE (fora da pílula). As demais recebem a cor da linha
        // pela CLASSE bgClass (em vez de style inline repetido).
        if (col !== 'position' && bgClass) { classes.push(bgClass); }
        const cls = classes.length ? ` class="${classes.join(' ')}"` : '';
        return `<td${cls}>${inner}</td>`;
      }).join('');
      return `<tr>${cells}</tr>`;
    }).join('');

    const ev: any = this.event;
    const eventLabel = this.escapeHtml(`${ev?.acronym || ''} ${ev?.year || ''}`.trim());
    const heading = this.escapeHtml(this.translate.instant('admin.event.submissions.submissions-list.title') || 'Submissões');
    const fullTitle = heading + (eventLabel ? ' — ' + eventLabel : '');

    // Tema do export: espelha o dark-mode da PÁGINA (classe .dark-theme no <body>).
    const isDark = typeof document !== 'undefined' && !!document.body && document.body.classList.contains('dark-theme');
    // Linhas COLORIDAS (Colorir ON) têm fundo de status CLARO em AMBOS os temas → o conteúdo
    // (texto/links) fica no esquema LIGHT mesmo em dark, igual à página (_color-scheme.scss
    // ~L2270). Só dark + Colorir OFF (linhas planas escuras) usa o esquema de CONTEÚDO escuro.
    const darkContent = isDark && !this.colorRowsByStatus;

    // Cor dos links: no claro / linhas-coloridas, herda --jems-link-color do sistema em runtime
    // (= $accent-background; muda sozinho se a cor do sistema mudar). Em dark + Colorir OFF usa
    // o accent dark ($accent-background-dark = #FBBF24), legível sobre fundo escuro.
    const cssLinkColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--jems-link-color').trim() || '#1f3a93';
    const linkColor = darkContent ? '#FBBF24' : cssLinkColor;

    // Paleta theme-aware (espelha os tokens da página). O "chrome" (fundo/cabeçalho/heading/
    // legenda) segue o tema SEMPRE; o CONTEÚDO das células só escurece quando darkContent.
    const cChromeText = isDark ? '#F6F6F6' : '#373734';           // texto de chrome (h1/body) — $content-color[-dark]
    const cText      = darkContent ? '#c4c2bd' : '#373734';       // texto-base das células ($sidebar-admin-menuitem-color[-dark])
    const cTextMuted = darkContent ? '#9a9893' : '#6b6a66';       // texto auxiliar (respostas do formulário)
    const cHeader    = isDark ? '#FFFFFFB3' : 'rgba(0,0,0,.54)';  // cabeçalho + coluna # ($table-header-color[-dark])
    const cBodyBg    = isDark ? '#000000' : '#fff';               // fundo = bg da tabela em dark ($content-background-dark = black)
    const grayBg     = isDark ? '#202020' : SubmissionsListTableV2Component.EXPORT_NO_STATUS_COLOR; // linha sem status ($sidebar-admin-background-dark / $gray-125)
    const cLegendLbl = isDark ? 'rgba(255,255,255,.5)' : 'rgba(0,0,0,.5)';
    const cTopicBg   = isDark ? '#3a3937' : '#ededed';            // chip de tópico (fundo)
    const cTopicText = isDark ? '#c4c2bd' : '#363636';            // chip de tópico (texto)
    // Mini-tabela de scores (grid): fundo das CÉLULAS = cinza ($gray-100) com "Colorir" ligado,
    // branco com desligado (igual à tela); pill cinza da inicial do critério no cabeçalho.
    const scCellBg = isDark ? '#242424' : (this.colorRowsByStatus ? '#F5F5F4' : '#fff');
    const scPillBg = isDark ? 'rgba(255,255,255,.14)' : '#E7E5E4';   // $gray-200
    const scSep    = isDark ? 'rgba(255,255,255,.10)' : 'rgba(0,0,0,.08)'; // separador de coluna

    // CSS mínimo, próximo do estilo da PÁGINA do sistema: linhas com pontas
    // ARREDONDADAS (6px = $table-row-border-radius), separadas por GAP transparente
    // (border-spacing, sem bordas/divisórias visíveis); CABEÇALHO SEM FUNDO (12px/500,
    // centralizado, na base — como .mat-header-cell). Base 13px / #373734 / Geist.
    // Fonte: o Geist (fonte local do app) NÃO é embutido — o HTML usa o fallback do
    // sistema (-apple-system/Segoe UI). Os PESOS-valor já batem com a página (base 400,
    // título 600, "Resumo." 700); só a renderização da família difere por usar o
    // fallback. (Decisão: não embutir nem depender de internet/Google Fonts.)
    //
    // CSS DINÂMICO (só pros status/classes PRESENTES) — substitui o style inline
    // repetido por célula: cor de fundo da linha (.bg-<status> / .bg-g cinza), faixa
    // accent da coluna ID (.st-<status>, gradient) e cor do chip de status (.ck-<status>,
    // clareada/cheia conforme o toggle). É o que mais encolhe o HTML.

    // FONTE ÚNICA de estilos: array [seletor, declarações] que gera TANTO o <style>
    // do download QUANTO o inline do clipboard (via inlineExportStyles) — os dois
    // formatos saem sempre da mesma definição. Valores espelham o sistema (base
    // 13px/#373734/Geist; cabeçalho .mat-header-cell rgba(0,0,0,.54); chip
    // _status-chip 104x20; pill de span; chips de tópicos; etc.).
    const rules: Array<[string, string]> = [
      // background SÓ no download; no COPY (inline) fica TRANSPARENTE pra blendar com o destino
      // (o payload do clipboard também é o doc completo com <body>+<style>, e clientes como o
      // Mac Mail aplicam a regra `body` ao colar → era daí que vinha o retângulo preto).
      ['body', `font-family:Geist,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:13px;color:${cChromeText};margin:20px;background:${opts.inline ? 'transparent' : cBodyBg}`],
      ['h1', 'font-size:18px;font-weight:600;margin:0 0 16px'],
      // SEM `width:100%`: a tabela dimensiona pelo CONTEÚDO (não força caber na página), então a
      // coluna de descrição NÃO é comprimida pra dar espaço à mini-tabela; a página rola na
      // horizontal quando passa da largura — como o container de scroll do sistema.
      ['table', 'border-collapse:separate;border-spacing:1px 1px'],
      // font-family/size/color repetidos em th e td (não só no body/wrapper): clientes
      // de e-mail (Mac Mail/Outlook) NÃO herdam font dentro de <table> — é preciso
      // declarar no nível da célula, senão o texto cai no tamanho padrão do cliente.
      ['th', `font-family:Geist,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:12px;font-weight:500;line-height:16px;letter-spacing:0;text-transform:none;font-feature-settings:"tnum";padding:2px 8px;text-align:center;vertical-align:bottom;border:0;background:none;color:${cHeader};white-space:nowrap`],
      ['td', `font-family:Geist,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:13px;color:${cText};padding:7px 9px;border:0;vertical-align:middle;text-align:left`],
      ['td.c', 'text-align:center;white-space:nowrap'],
      // Coluna # (numeração): mesma tipografia/cor do cabeçalho (token $table-header-color).
      ['td.num', `font-size:12px;font-weight:500;color:${cHeader}`],
      // Sem `width:100%` na tabela, a coluna de descrição pegaria a largura da MAIOR linha sem
      // quebrar (título/autores/resumo numa linha só = coluna enorme). O `max-width` faz o conteúdo
      // QUEBRAR nessa largura (como no sistema); o `min-width` evita que fique estreita demais.
      ['td.desc', 'vertical-align:top;min-width:440px;max-width:560px'],
      ['td.round-l', 'border-top-left-radius:6px;border-bottom-left-radius:6px'],
      ['tr td:last-child', 'border-top-right-radius:6px;border-bottom-right-radius:6px'],
      // text-decoration:none com !important: clientes de e-mail (Mac Mail) forçam
      // sublinhado em links com !important próprio — só um !important (inline, no
      // clipboard) vence. No download é inócuo (já não havia sublinhado).
      ['a', `color:${linkColor};text-decoration:none !important`],
      ['.title', 'font-weight:600;font-size:14px'],
      // Link do título (submissão) usa a COR DE LINK do sistema (regra `a` genérica acima),
      // igual aos demais links — sem override de cor aqui. Mantém negrito/14px do `.title`.
      ['.affiliation', `color:${cText}`],
      // `min-width:500px` = MESMO valor/lugar do sistema (`.abstract` no scss do componente): dá
      // um piso à coluna de descrição quando o resumo aparece, então ela NÃO comprime pra caber a
      // mini-tabela de scores. Como a janela do download já é um container de scroll horizontal, a
      // tabela transborda e a página rola — comportamento idêntico ao do sistema.
      ['.abstract', 'display:block;font-style:italic;text-align:justify;margin-top:.75rem;min-width:500px'],
      ['.abstract>span', 'font-weight:700'],
      ['.topics', 'display:block;margin-top:10px'],
      ['.topic-chip', `display:inline-flex;align-items:center;min-height:1.4rem;padding:0 10px;margin:0 5px 4px 0;border-radius:16px;background:${cTopicBg};color:${cTopicText};font-size:12px;font-weight:400;box-shadow:0 0 1px rgba(0,0,0,.5);white-space:nowrap`],
      ['.chip', 'box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;width:96px;height:22px;padding:0 8px;border-radius:999px;font-size:11px;font-weight:500;line-height:1;letter-spacing:0;color:#434343;border-top:1px solid rgba(255,255,255,.6);border-left:1px solid rgba(255,255,255,.6);border-right:1px solid transparent;border-bottom:1px solid transparent;box-shadow:0 1px 2px rgba(0,0,0,.55);white-space:nowrap;gap:5px'],
      ['.chip-ico', 'width:13px;height:13px;flex:0 0 auto'],
      ['.span-pill', 'display:block;padding:2px 8px;border-radius:4px;font-weight:400;color:#373734;line-height:1.3'],
      // Mini-tabela de scores (coluna "Notas individuais") — CSS GRID espelhando a `.score-grid`
      // da tela: CABEÇALHO (`.sg-head`, no <th>, UMA vez) e CORPO (`.sg`, com linhas `.sg-row` em
      // SUBGRID) usam o MESMO grid-template-columns (inline = scoreGridCols) → colunas alinhadas.
      // Fonte 12px/1.5 (= 0.75rem da tela); cada linha é um "pill" com SOMBRA + pontas arredondadas
      // (sg-rl/sg-rr); row-gap 1px deixa a cor da linha (do <td> atrás) aparecer entre as linhas.
      ['.sg', 'display:grid;row-gap:1px;column-gap:0;width:max-content;max-width:100%;margin:0 auto;font-size:12px;line-height:1.5'],
      ['.sg-row', `display:grid;grid-column:1/-1;grid-template-columns:subgrid;align-items:stretch;border-radius:4px;box-shadow:${isDark ? 'none' : '0 1px 3px rgba(0,0,0,.18)'}`],
      ['.sg-row > *', `display:flex;align-items:center;justify-content:center;min-height:18px;padding:0 4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:${cText};background:${scCellBg};border-right:1px solid ${scSep};box-sizing:border-box`],
      ['.sg-row > .scr', 'justify-content:flex-start'],
      ['.sg-row > .sg-rl', 'border-top-left-radius:4px;border-bottom-left-radius:4px'],
      ['.sg-row > .sg-rr', 'border-top-right-radius:4px;border-bottom-right-radius:4px;border-right:0'],
      ['.sg-row.sg-foot > *', 'font-weight:600'],
      ['.sg-row.sg-foot > .sg-rl', 'justify-content:flex-end'],
      // Pill de status de revisão: quadrado 16px colorido (bg = cor CLAREADA, borda = cor ESCURECIDA
      // de $review-status, ambas inline, como a tela) + `title` = texto (ícone de fonte não embute).
      ['.sg .scst', 'display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;border-radius:3px;border:1px solid;box-sizing:border-box'],
      ['.sg .scst-ico', 'display:block'],
      // Cabeçalho (no <th>): mesma grade, bottom-alinhado; inicial do critério num pill cinza 20px
      // (`title` = legenda completa, como o tooltip da tela). Rótulos podem quebrar linha.
      ['.sg-head', 'display:grid;align-items:end;column-gap:0;width:max-content;margin:0 auto;font-size:12px;line-height:1.2'],
      ['.sg-head > *', `display:flex;align-items:flex-end;justify-content:center;padding:0 4px;text-align:center;color:${cHeader};white-space:normal;word-break:break-word;line-height:1.2`],
      ['.sg-head > .scr', 'justify-content:flex-start;text-align:left'],
      ['.sg-head .sg-pill', `position:relative;display:inline-flex;align-items:center;justify-content:center;min-width:20px;height:20px;padding:0 4px;border-radius:4px;background:${scPillBg};color:${cText};font-weight:600;font-size:12px;line-height:1;cursor:default`],
      // Tooltip da legenda do critério (hover CSS): balão cinza estilizado como o `.sg-crit-tooltip`
      // da tela; multi-linha (pre-line, alinhado à esquerda); abaixo do pill (o cabeçalho fica no topo).
      ['.sg-head .sg-tip', `display:none;position:absolute;top:100%;left:50%;transform:translateX(-50%);margin-top:6px;z-index:20;width:max-content;max-width:260px;padding:6px 8px;border-radius:4px;background:rgba(97,97,97,.95);color:#fff;font-size:11px;font-weight:400;line-height:1.4;white-space:pre-line;text-align:left;box-shadow:0 2px 8px rgba(0,0,0,.25);pointer-events:none`],
      ['.sg-head .sg-pill:hover .sg-tip', 'display:block'],
      ['.ff-item', 'margin:0 0 8px'],
      ['.ff-item:last-child', 'margin-bottom:0'],
      ['.ff-title', 'font-weight:600'],
      ['.ff-desc', `color:${cText}`],
      ['.ff-ans', `margin-top:2px;color:${cTextMuted}`],
      ['.legend', 'margin-top:14px;display:flex;flex-direction:column;align-items:flex-start;gap:.5em'],
      ['.lg-row', 'display:flex;align-items:center;flex-wrap:wrap;gap:6px'],
      ['.lg-label', `font-size:12px;font-weight:400;color:${cLegendLbl}`],
      ['.lg-chip', 'display:inline-flex;align-items:center;padding:2px 8px;border-radius:6px;font-size:12px;color:#373734;white-space:nowrap'],
    ];
    // Regras DINÂMICAS (só pros status/classes PRESENTES): cor de fundo da linha
    // (.bg-*/.bg-g), faixa accent da coluna ID (.st-*) e cor do chip de status (.ck-*).
    const dynRules: Array<[string, string]> = [['.bg-g', `background-color:${grayBg}`]];
    colorClassSet.forEach(c => {
      dynRules.push([`.bg-${c}`, `background-color:${colors[c]}`]);
      dynRules.push([`.st-${c}`, `background-image:linear-gradient(to right,${colors[c]} 5px,transparent 5px)`]);
    });
    statusSet.forEach(st => {
      if (colors[st]) {
        const chipColor = this.colorRowsByStatus ? this.lightenHex(colors[st], 0.5) : colors[st];
        dynRules.push([`.ck-${st}`, `background-color:${chipColor}`]);
      }
    });
    const allRules = [...rules, ...dynRules];
    const cssText = allRules.map(([sel, decl]) => `${sel}{${decl}}`).join('');

    // Ícones de status definidos UMA vez (<symbol>), referenciados por <use> em cada chip.
    let symbols = '';
    statusSet.forEach(st => {
      const ic = icons[st];
      if (ic) {
        const col = iconColors[st] || '#434343';
        symbols += `<symbol id="st-${st}" viewBox="${ic.vb}"><path d="${ic.d}" fill="${col}"/></symbol>`;
      }
    });
    // Ícones de status de REVISÃO (pills da mini-tabela de scores): um <symbol> por status PRESENTE
    // nas revisões, com fill = cor darken(40%) do status. Referenciados por <use> em cada pill.
    const revStatusSet = new Set<string>();
    rows.forEach(item => ((item.rank as any)?.byReview?.reviews || [])
      .forEach((r: any) => { if (r.status) { revStatusSet.add(String(r.status).toLowerCase()); } }));
    const revSvgs = SubmissionsListTableV2Component.EXPORT_REVIEW_STATUS_SVG;
    const revPills = SubmissionsListTableV2Component.EXPORT_REVIEW_STATUS_PILL;
    revStatusSet.forEach(st => {
      const svg = revSvgs[st];
      if (svg) {
        const fg = (revPills[st] || { fg: '#606060' }).fg;
        symbols += `<symbol id="rst-${st}" viewBox="${svg.vb}"><path d="${svg.d}" fill="${fg}"/></symbol>`;
      }
    });
    if (symbols) { symbols = `<svg width="0" height="0" style="position:absolute" aria-hidden="true">${symbols}</svg>`; }

    // Legenda no rodapé da PRÓPRIA tabela (<tfoot>): a 1ª célula (vazia) ocupa a
    // coluna "#", e a legenda vai numa célula com colspan no restante. Assim ela
    // alinha AUTOMATICAMENTE com a 2ª coluna, qualquer que seja a largura do "#"
    // (que é dinâmica no HTML). Fallback p/ tabela de coluna única.
    const legendInner = opts.legend ? this.exportLegendHtml() : '';
    const tfoot = !opts.legend ? '' : (cols.length > 1
      ? `<tfoot><tr class="lg-foot"><td></td><td colspan="${cols.length - 1}">${legendInner}</td></tr></tfoot>`
      : `<tfoot><tr class="lg-foot"><td>${legendInner}</td></tr></tfoot>`);

    const headingHtml = opts.heading ? `<h1>${fullTitle}</h1>` : '';

    let tableBlock = `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody>${tfoot}</table>`;
    if (opts.inline) {
      // Caminho INLINE (clipboard): aplica TODAS as regras como style="" (o navegador
      // casa os seletores), p/ sobreviver a clientes que removem <style> (Gmail web
      // etc.). Envolve numa div com a tipografia base (que viria do body) p/ as
      // células herdarem fonte/cor.
      tableBlock = this.inlineExportStyles(tableBlock, allRules);
      // SEM background no wrapper do clipboard (transparente): ao colar, blenda com o fundo
      // do destino (e-mail/doc) em vez de cravar um retângulo. Só o DOWNLOAD tem fundo (regra
      // `body` via <style>; o fragmento colado não tem <body>, então o `body` não o atinge).
      tableBlock = `<div style="font-family:Geist,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:13px;color:${cChromeText}">${tableBlock}</div>`;
    }

    const doc =
      '<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">' +
      `<title>${fullTitle}</title><style>${cssText}</style></head><body>` +
      symbols +
      headingHtml +
      tableBlock +
      '</body></html>';

    return doc;
  }

  // Aplica as regras [seletor, declarações] como `style=""` INLINE em cada elemento
  // do HTML (deixa o navegador fazer o matching dos seletores — descendentes,
  // last-child, td.c, etc.). Usado no copiar p/ clipboard, p/ o estilo sobreviver a
  // clientes de e-mail que removem o bloco <style>. Ordem das regras = cascata
  // aproximada (declarações posteriores sobrescrevem as anteriores).
  private inlineExportStyles(html: string, rules: Array<[string, string]>): string {
    const container = document.createElement('div');
    container.innerHTML = html;
    const append = (node: Element, decls: string) => {
      const prev = node.getAttribute('style') || '';
      const sep = prev && !prev.trim().endsWith(';') ? ';' : '';
      node.setAttribute('style', prev + sep + decls);
    };
    // Preserva a PRECEDÊNCIA da cascata: o style inline ORIGINAL do autor (ex.: a cor
    // #ffeeee do span da faixa vermelha) vence as regras de classe. Tira-se o original,
    // aplicam-se as regras, e o original é reanexado por ÚLTIMO (último = vence).
    const orig = new Map<Element, string>();
    container.querySelectorAll('[style]').forEach(el => { orig.set(el, el.getAttribute('style') || ''); el.removeAttribute('style'); });
    for (const [selector, decls] of rules) {
      let nodes: NodeListOf<Element>;
      try { nodes = container.querySelectorAll(selector); } catch (e) { continue; }
      nodes.forEach(node => append(node, decls));
    }
    orig.forEach((style, el) => append(el, style));
    return container.innerHTML;
  }

  // ============================================================
  // Copiar linhas selecionadas para a área de transferência (CTRL+C)
  // ============================================================
  // Intercepta o evento `copy` do documento: havendo linhas MARCADAS (checkbox) e
  // SEM seleção de texto / foco em input, escreve no clipboard o MESMO HTML do
  // download (cores, chips de status, pill de span, fonte) — só das linhas
  // selecionadas, sem h1/legenda — mais um text/plain (TSV) como fallback. Assim,
  // colar no Excel/Word/e-mail traz a tabela estilizada idêntica à exportação.
  @HostListener('document:copy', ['$event'])
  onDocumentCopy(event: ClipboardEvent): void {
    const all = this.dataSource?.data || [];
    if (!all.some(item => item.selected)) { return; }   // sem seleção → cópia padrão
    // Não sequestra cópia de seleção de texto nem de campos de TEXTO (textarea/
    // contenteditable/input de texto). Atenção: o checkbox do mat-checkbox é um
    // <input type="checkbox"> — NÃO pode bloquear (senão o CTRL+C logo após marcar
    // a linha não funcionaria), por isso excluímos checkbox/radio/botões.
    const ae = document.activeElement as HTMLInputElement | null;
    const nonText = ['checkbox', 'radio', 'button', 'submit', 'range', 'color', 'file'];
    const isTextField = !!ae && (
      ae.tagName === 'TEXTAREA' ||
      (ae as HTMLElement).isContentEditable ||
      (ae.tagName === 'INPUT' && !nonText.includes((ae.type || '').toLowerCase()))
    );
    if (isTextField) { return; }
    if ((window.getSelection()?.toString() || '').trim().length) { return; }

    const rows = this.getDisplayedRows().filter(r => r.selected);
    if (!rows.length || !event.clipboardData) { return; }
    const payload = this.clipboardRowsPayload(rows);
    event.clipboardData.setData('text/html', payload.html);
    event.clipboardData.setData('text/plain', payload.text);
    event.preventDefault();
    this.flashCopyFeedback();
  }

  // Copiar selecionadas via BOTÃO (barra de ações). Um clique NÃO é evento `copy`, então usa a
  // Clipboard API assíncrona (ClipboardItem com text/html + text/plain). Mesmo
  // conteúdo do CTRL+C. (ClipboardItem não é tipado no TS 4.1 → acesso via `any`.)
  public async copySelectedToClipboard(): Promise<void> {
    const rows = this.getDisplayedRows().filter(r => r.selected);
    if (!rows.length) { this.notificationService.notify('submissions.ranking.copy.empty'); return; }
    const payload = this.clipboardRowsPayload(rows);
    try {
      const ClipboardItemCtor = (window as any).ClipboardItem;
      if (navigator.clipboard && ClipboardItemCtor) {
        const item = new ClipboardItemCtor({
          'text/html': new Blob([payload.html], { type: 'text/html' }),
          'text/plain': new Blob([payload.text], { type: 'text/plain' }),
        });
        await (navigator.clipboard as any).write([item]);
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(payload.text);   // fallback: só texto
      } else {
        throw new Error('Clipboard API indisponível');
      }
      this.flashCopyFeedback();
    } catch (e) {
      this.notificationService.notifyError('submissions.ranking.copy.error');
    }
  }

  // Payload do clipboard (HTML inline fiel + TSV), das linhas e SEM a coluna "#". Os ícones de status
  // são SVG inline (<symbol>/<use>), que sobrevivem a e-mail — sem depender de fonte.
  private clipboardRowsPayload(rows: SubmissionRanking[]): { html: string, text: string } {
    return {
      html: this.buildExportHtmlDoc(rows, { heading: false, legend: false, inline: true, includePosition: false }),
      text: this.buildClipboardPlainText(rows),
    };
  }

  // Feedback de cópia bem-sucedida = ANIMAÇÃO DO BOTÃO "Copiar" na barra flutuante (ícone vira
  // check + "Copiado!" por ~1s), igual à página Submissões do autor/revisor. SEM a barra verde
  // de notificação (removida a pedido). Cobre os dois caminhos (botão e CTRL+C).
  private flashCopyFeedback(): void {
    this.actionBarComponent?.flashCopied();
  }

  // Notificar TODAS as submissões pendentes do evento (independe de seleção) — vai pra
  // tela de notificação com os IDs de status PENDING. Acionado pelo menu de Ações do
  // header. (A barra flutuante só age sobre as linhas selecionadas; aqui é o evento todo.)
  public notifyPendingSubmissions(): void {
    const ids = (this.dataSource?.data || [])
      .filter(item => item.submission.status === SubmissionStatus.PENDING)
      .map(item => item.submission.id);
    const ev: any = this.event;
    this.router.navigate([`/admin/${ev?.id}/submissions/notify-pending`], { queryParams: { ids: JSON.stringify(ids), eventID: ev?.id } });
  }

  // Fallback text/plain (TSV) das linhas — colunas separadas por TAB, com cabeçalho.
  // Reaproveita exportCellText (mesma extração textual do Excel); o índice "#" é o do
  // ranking COMPLETO (getDisplayedRows), igual ao HTML.
  private buildClipboardPlainText(rows: SubmissionRanking[]): string {
    // Sem 'select' e sem 'position' (a coluna "#" não vai no copy, só no download).
    const cols = (this.displayedColumns || []).filter(col => col !== 'select' && col !== 'position' && this.exportColumnVisible(col));
    const rankIndex = new Map<SubmissionRanking, number>();
    this.getDisplayedRows().forEach((r, i) => rankIndex.set(r, i));
    const clean = (s: string) => (s || '').replace(/\t/g, ' ').replace(/\r?\n/g, ' ').trim();
    const header = cols.map(col => clean(col === 'description' ? this.exportDescriptionHeader() : this.exportHeader(col))).join('\t');
    const body = rows.map(item => {
      const index = rankIndex.get(item) ?? 0;
      return cols.map(col => clean(this.exportCellText(col, item, index))).join('\t');
    }).join('\n');
    return header + '\n' + body;
  }

  // Legenda do rodapé (HTML), espelhando o footer da tabela do sistema:
  //   "Status da submissão:"  + chip por status (active/pending/accepted/rejected/withdrawn)
  //   "Revisões conflitantes:" + chip por faixa de span — SÓ se a coluna Span estiver visível
  //     (igual ao [hidden]="!isSetField(['score-span'])" do template).
  // Rótulo recuado (muted) + chips coloridos = mesmo tratamento da página. As contagens
  // reusam subtitleValues/conflictSubtitleValues (filtram o dataSource visível). As cores
  // vêm dos mesmos mapas usados nas linhas exportadas (EXPORT_ROW_COLORS / EXPORT_SPAN_COLORS).
  private exportLegendHtml(): string {
    const rowColors = SubmissionsListTableV2Component.EXPORT_ROW_COLORS;
    const spanColors = SubmissionsListTableV2Component.EXPORT_SPAN_COLORS;
    const txt = (key: string, n: number) => this.escapeHtml(this.translate.instant(key, { n }));
    const lbl = (key: string) => this.escapeHtml(this.translate.instant(key));
    const chip = (bg: string, content: string, color?: string) =>
      `<span class="lg-chip" style="background:${bg}${color ? ';color:' + color : ''}">${content}</span>`;

    // Linha 1 — status (sempre). Ordem idêntica à da página.
    const statuses: Array<[SubmissionStatus, string, string]> = [
      [SubmissionStatus.ACTIVE, 'active', 'reports.submissions.ranking.active'],
      [SubmissionStatus.PENDING, 'pending', 'reports.submissions.ranking.pending'],
      [SubmissionStatus.ACCEPTED, 'accepted', 'reports.submissions.ranking.accepted'],
      [SubmissionStatus.REJECTED, 'rejected', 'reports.submissions.ranking.rejected'],
      [SubmissionStatus.WITHDRAWN, 'withdrawn', 'reports.submissions.ranking.withdrawn'],
    ];
    const statusChips = statuses
      .map(([st, cls, key]) => chip(rowColors[cls], txt(key, this.subtitleValues(st))))
      .join('');
    let rows = `<div class="lg-row"><span class="lg-label">${lbl('reports.submissions.ranking.legend')}:</span>${statusChips}</div>`;

    // Linha 2 — revisões conflitantes (só se a coluna Span estiver visível).
    if (this.isSetField(['score-span'])) {
      const conflicts: Array<['span-under-A' | 'span-between-A-and-B' | 'span-above-B', string]> = [
        ['span-under-A', 'reports.submissions.conflicting-reviews.not-conflicting'],
        ['span-between-A-and-B', 'reports.submissions.conflicting-reviews.may-have-conflicts'],
        ['span-above-B', 'reports.submissions.conflicting-reviews.has-serious-conflicts'],
      ];
      const conflictChips = conflicts
        .map(([cls, key]) => {
          const c = spanColors[cls];
          return chip(c.bg, txt(key, this.conflictSubtitleValues(cls)), c.color);
        })
        .join('');
      rows += `<div class="lg-row"><span class="lg-label">${lbl('reports.submissions.conflicting-reviews.legend')}:</span>${conflictChips}</div>`;
    }
    return `<div class="legend">${rows}</div>`;
  }

  // Cabeçalho VARIÁVEL da coluna de descrição (HTML), igual ao navegador:
  // "Título" + " / Autores" + " / Tópicos" + " / Resumo" conforme os sub-campos visíveis.
  private exportDescriptionHeader(): string {
    const parts: string[] = [];
    if (this.isSetField(['description'])) { parts.push(this.translate.instant('forms.fields.title')); }
    if (this.isSetField(['authors-names', 'authors-affiliations', 'authors-countries', 'authors-emails'])) {
      parts.push(this.translate.instant('forms.fields.authors'));
    }
    if (this.isSetField(['topics'])) { parts.push(this.translate.instant('forms.fields.topics')); }
    if (this.isSetField(['abstract'])) { parts.push(this.translate.instant('forms.fields.abstract')); }
    return parts.join(' / ');
  }

  // Descrição combinada (HTML): título + autores (inline) + resumo + tópicos, conforme
  // os sub-campos visíveis — espelha o que o navegador mostra na célula.
  private exportDescriptionHtml(item: SubmissionRanking): string {
    const s: any = item.submission;
    // Título + autores ficam inline (separados por <br>, colados como na página);
    // Resumo e Tópicos são BLOCOS (display:block) com respiro próprio acima
    // (.abstract margin-top .75rem, .topics margin-top 10px) — espelhando o sistema.
    // O TÍTULO é o link da submissão (antes o link ficava no #ID — movido p/ cá, e o ID
    // ficou sem link). URL absoluta (origin) porque o HTML é colado em e-mail/standalone.
    let html = '';
    if (this.isSetField(['description'])) {
      const href = this.escapeHtml(this.exportSubmissionHref(item));
      html = `<span class="title"><a href="${href}" target="_blank" rel="noopener">${this.escapeHtml(s.title || '')}</a></span>`;
    }
    if (this.isSetField(['authors-names', 'authors-affiliations', 'authors-countries', 'authors-emails'])) {
      const authors = this.exportAuthorsHtml(item);
      // `<br>` só se já houver o título antes (senão a linha começaria com quebra).
      if (authors) { html += html ? `<br>${authors}` : authors; }
    }
    if (this.isSetField(['abstract']) && s.abstract) {
      // Igual à página: rótulo "Resumo." em negrito + texto do resumo, tudo em itálico.
      const label = this.escapeHtml(this.translate.instant('forms.fields.abstract'));
      html += `<span class="abstract"><span>${label}. </span>${this.escapeHtml(s.abstract)}</span>`;
    }
    if (this.isSetField(['topics']) && s.topics?.length) {
      // Tópicos como CHIPS (pílulas cinza), iguais aos mat-chip da página.
      const chips = s.topics.map((t: any) => t.name).filter(Boolean)
        .map((n: string) => `<span class="topic-chip">${this.escapeHtml(n)}</span>`).join('');
      html += `<span class="topics">${chips}</span>`;
    }
    return html;
  }

  // Autores (HTML), fiel à página: nome → link da PESSOA (/admin/{ev}/people/{userId}); e-mail
  // → link mailto. DOIS links separados (antes era um único mailto envolvendo nome+e-mail).
  // Afiliação e país (por extenso via countryPipe) em texto normal entre parênteses.
  private exportAuthorsHtml(item: SubmissionRanking): string {
    const s: any = item.submission;
    const ev: any = this.event;
    // URL ABSOLUTA (origin): o HTML é colado em e-mail / aberto standalone, então link relativo
    // não resolveria — mesmo critério do link do título (exportSubmissionHref).
    const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
    return (s.authors || []).map((a: any) => {
      const u = a.user || {};
      const namePart = this.isSetField(['authors-names']) ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : '';
      const emailRaw = this.isSetField(['authors-emails']) ? (u.email || '') : '';
      // Nome → link da pessoa (se houver userId; senão texto puro).
      const nameHtml = namePart
        ? (u.id != null
            ? `<a href="${this.escapeHtml(`${origin}/admin/${ev?.id}/people/${u.id}`)}" target="_blank" rel="noopener">${this.escapeHtml(namePart)}</a>`
            : this.escapeHtml(namePart))
        : '';
      // E-mail → mailto (entre < >), só quando authors-emails está visível.
      const emailHtml = emailRaw
        ? `<a href="mailto:${this.escapeHtml(emailRaw)}">&lt;${this.escapeHtml(emailRaw)}&gt;</a>` : '';
      const person = [nameHtml, emailHtml].filter(Boolean).join(' ');
      const aff = this.isSetField(['authors-affiliations']) ? (u.profile?.affiliation?.acronym || '') : '';
      const countryCode = this.isSetField(['authors-countries']) ? (u.profile?.country || '') : '';
      const country = countryCode ? (this.countryPipe.transform(countryCode) || '') : '';
      const extra = [aff, country].filter(Boolean).map((x: string) => this.escapeHtml(x)).join(', ');
      const affHtml = extra ? `${person ? ' ' : ''}<span class="affiliation">(${extra})</span>` : '';
      return `${person}${affHtml}`.trim();
    }).filter(Boolean).join(', ');
  }

  // Status como CHIP (pílula): nome traduzido + ícone. Pra ENCOLHER o HTML, a COR do
  // chip vem de uma classe `.ck-<status>` (emitida 1× no CSS, conforme o toggle) e o
  // ÍCONE de um `<symbol>` referenciado por `<use>` (definido 1× no topo) — em vez de
  // repetir o path SVG e a cor inline em cada linha. As regras/symbols vêm do
  // exportTableHtml (baseado nos status presentes).
  private exportStatusChipHtml(item: SubmissionRanking): string {
    const s: any = item.submission;
    const key = String(s.status).toLowerCase();
    // width/height como ATRIBUTOS (não só via classe .chip-ico): SVG não dimensiona
    // de forma confiável por CSS em alguns renderizadores (ex.: conteúdo colado no
    // Mac Mail) — o atributo garante o tamanho. O símbolo já tem o viewBox.
    const svg = SubmissionsListTableV2Component.EXPORT_STATUS_ICONS[key]
      ? `<svg class="chip-ico" width="13" height="13" aria-hidden="true"><use href="#st-${key}"/></svg>`
      : '';
    const name = this.escapeHtml(this.translate.instant('status.' + s.status));
    return `<span class="chip ck-${key}">${svg}<span>${name}</span></span>`;
  }

  // Href da página da submissão (mesma rota do sistema: /admin/{eventId}/submissions/{id}),
  // em URL ABSOLUTA (origin) — o HTML é aberto standalone / colado em e-mail, então link
  // relativo não resolveria. Usado no link do TÍTULO no export (o ID ficou sem link).
  private exportSubmissionHref(item: SubmissionRanking): string {
    const id = item.submission.id;
    const ev: any = this.event;
    const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
    return `${origin}/admin/${ev?.id}/submissions/${id}`;
  }

  // Span como PILL (coluna Span): número dentro de uma pílula colorida por faixa,
  // igual à página (.score-span + .span-under-A/.span-above-B/.span-between-A-and-B).
  // Sem valor (NaN) → texto simples, sem pílula.
  private exportSpanPillHtml(item: SubmissionRanking): string {
    const span = item.rank?.span ?? NaN;
    const text = this.escapeHtml(this.showRankValue(span));
    const cls = this.rankingSpanClass(span);
    const c = cls ? SubmissionsListTableV2Component.EXPORT_SPAN_COLORS[cls] : undefined;
    if (!c) { return text; }
    const style = `background-color:${c.bg}` + (c.color ? `;color:${c.color}` : '');
    return `<span class="span-pill" style="${style}">${text}</span>`;
  }

  // Itens do formulário da submissão (coluna others-form): mesma fonte do template
  // (getSubmissionFormAnswers), já com as respostas nulas (checkbox não marcado) e
  // vazias removidas. Protegido (sem formulário carregado → lista vazia).
  private exportFormItems(item: SubmissionRanking): Array<{ order: number; shortLabel: string; description: string; answers: string[] }> {
    try {
      const list = item.submission.getSubmissionFormAnswers(this.submissionForm) || [];
      return list.map((f: any) => ({ ...f, answers: (f.answers || []).filter((v: any) => v != null && v !== '') }));
    } catch {
      return [];
    }
  }

  // Itens do formulário (HTML), fiel ao template: por campo, "N) rótulo: descrição"
  // (título) seguido de cada resposta numa linha própria.
  private exportFormItemsHtml(item: SubmissionRanking): string {
    const items = this.exportFormItems(item);
    if (!items.length) { return ''; }
    return items.map(f => {
      const title = `<span class="ff-title">${this.escapeHtml((f.order + 1) + ') ' + f.shortLabel)}: </span>` +
                    `<span class="ff-desc">${this.escapeHtml(f.description || '')}</span>`;
      const ans = f.answers.map(v => `<div class="ff-ans">${this.escapeHtml(v)}</div>`).join('');
      return `<div class="ff-item">${title}${ans}</div>`;
    }).join('');
  }

  // Mistura uma cor #RRGGBB com branco (amount 0..1) — usado pra clarear o chip.
  private lightenHex(hex: string, amount: number): string {
    const h = String(hex).replace('#', '');
    if (h.length < 6) { return hex; }
    const mix = (i: number) => {
      const c = parseInt(h.substr(i, 2), 16);
      return Math.round(c + (255 - c) * amount).toString(16).padStart(2, '0');
    };
    return '#' + mix(0) + mix(2) + mix(4);
  }

  // Arquivos (HTML): cada arquivo enviado vira um link (um por linha).
  private exportFilesHtmlLinks(item: SubmissionRanking): string {
    const s: any = item.submission;
    const trackId = s.track?.id;
    const trackFiles = trackId != null ? (this.getTrackFiles(trackId) || []) : [];
    return (trackFiles as any[]).map((tf: any) => {
      const file: any = this.getFile(s.files, tf.id);
      const name = this.escapeHtml(tf.name || file?.name || '');
      // Link para a PÁGINA DA SUBMISSÃO, não para o arquivo. Um documento
      // exportado é aberto fora da aplicação, então não pode baixar com o
      // token no cabeçalho; e o antigo `file.url` aponta para a rota da
      // biblioteca de criptografia, que exige cookie de sessão e responde
      // erro (issue #2376). Levar à submissão mantém o link útil — de lá o
      // download funciona — em vez de deixá-lo quebrado.
      return file ? `<a href="${this.escapeHtml(this.submissionPageUrl(s))}" target="_blank" rel="noopener">${name}</a>` : name;
    }).filter(Boolean).join('<br>');
  }

  /** Endereço da página da submissão, absoluto — o documento exportado é
   *  aberto fora da aplicação, então caminho relativo não serve. */
  private submissionPageUrl(s: any): string {
    return `${window.location.origin}/submissions/${s?.id}`;
  }

  private escapeHtml(value: any): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  private exportHeader(col: string): string {
    if (col === 'position') { return ''; }   // cabeçalho da coluna "#" em branco no download (HTML e Excel)
    if (col.startsWith('customFields-')) {
      return this.customFields.find(f => f.control === col)?.label || col;
    }
    const key = SubmissionsListTableV2Component.EXPORT_HEADER_KEYS[col];
    return key ? this.translate.instant(key) : col;
  }

  private exportCellText(col: string, item: SubmissionRanking, index: number): string {
    const s: any = item.submission;
    switch (col) {
      case 'position': return String(index + 1);
      case 'id': return '#' + s.id;
      case 'status': return this.translate.instant('status.' + s.status);
      case 'description': return this.isSetField(['description']) ? (s.title || '') : '';
      case 'authors': return this.exportAuthors(item);
      case 'abstract': return s.abstract || '';
      case 'topics': return (s.topics || []).map((t: any) => t.name).filter(Boolean).join(', ');
      case 'group': return this.paperGroupList?.find(g => g.id === s.paperGroup)?.value || '';
      case 'tpc-group': return this.TPCGroupList?.find(g => g.id === s.tpcGroup)?.value || '';
      case 'event': { const ev: any = this.event; return [ev?.acronym, ev?.year].filter(Boolean).join(' '); }
      case 'track': return s.track?.name || s.trackName || '';
      case 'rebuttal': return s.rebuttal ? this.translate.instant('submissions.rebuttal.rebuttalWritten') : '';
      case 'discussion-messages': return String(s.discussionMessages?.length ?? '');
      case 'reviews': return String(s.reviews?.length ?? '');
      case 'score-average-arithmetic': return this.showRankValue(item.rank?.average ?? NaN);
      case 'score-average-weighted': return this.showRankValue(item.rank?.weightedAverage ?? NaN);
      case 'score-span': return this.showRankValue(item.rank?.span ?? NaN);
      case 'score-numeric': return this.exportScoreText(item);
      case 'registration': return this.translate.instant(this.getControlStatus(s.authorRegistration));
      case 'copyright': return this.translate.instant(this.getControlStatus(s.copyrightIEEE));
      case 'others-form':
        // Itens do formulário da submissão: por campo, "N) rótulo: descrição" + cada
        // resposta numa linha (quebra de linha = wrapText no Excel).
        return this.exportFormItems(item).map(f => {
          const head = `${f.order + 1}) ${f.shortLabel}: ${f.description || ''}`.trim();
          return [head, ...f.answers].join('\n');
        }).join('\n');
      default:
        if (col.startsWith('customFields-')) {
          const id = this.customFields.find(f => f.control === col)?.id;
          const checked = s.checkListCustom?.find((c: any) => c.fieldId === id)?.status;
          return this.translate.instant(this.getControlStatus(!!checked));
        }
        return '';
    }
  }

  // Detalhamento de scores (coluna "Notas individuais") para EXPORT. Espelha o grid da
  // página: por revisor → status + notas por critério + Média + Peso; ao final, a linha de
  // médias. `scoreColumnLabels()` dá os critérios (mesma ordem de review.answers).
  // Versão TEXTO (Excel): uma revisão por linha (quebra \n = wrapText na célula).
  private exportScoreText(item: SubmissionRanking): string {
    const rank: any = item.rank;
    const reviews: any[] = rank?.byReview?.reviews || [];
    if (!reviews.length) { return ''; }
    const labels = this.scoreColumnLabels();
    const num = (v: any) => this.locNum(v);
    const avgLbl = this.translate.instant('forms.fields.score-average-arithmetic');
    const wLbl = this.translate.instant('forms.fields.weight');
    const none = '-';   // média nula → "-" (mesmo fallback da tela; antes era "Nenhum")

    const showReviewer = !!this.vis['reviewer-name'];   // espelha a coluna Revisor da tela
    const lines = reviews.map(r => {
      const status = r.status ? `[${this.translate.instant('status.' + r.status)}]` : '';
      const head = showReviewer ? `${r.reviewer?.name || ''} ${status}`.trim() : status;
      if (!r.answers || r.answers.length === 0) { return `${head} — —`; }
      const crit = labels.map((l, j) => `${l}: ${num(r.answers[j])}`).join(' · ');
      return `${head} — ${crit} | ${avgLbl}: ${num(r.average?.toFixed(2))} · ${wLbl}: ${num(r.weight?.toFixed(2))}`;
    });
    const footCrit = labels.map((l, j) => {
      const v = rank.getReviewFieldAverage(j);
      return `${l}: ${v ? num(v.toFixed(2)) : none}`;
    }).join(' · ');
    const fAvg = rank.average ? num(rank.average.toFixed(2)) : none;
    const fW = rank.weightedAverage ? num(rank.weightedAverage.toFixed(2)) : none;
    lines.push(`${avgLbl} — ${footCrit} | ${fAvg} / ${fW}`);
    return lines.join('\n');
  }

  // CABEÇALHO da mini-tabela de scores (grid `.sg-head`) — vai UMA vez no <th> da coluna, no topo
  // (os títulos das colunas de revisão não se repetem mais em cada linha). Mesmo grid-template do
  // corpo (scoreGridCols) → colunas alinhadas. Critério = inicial num pill cinza + `title` com a
  // legenda completa (título + valor: descrição), como o tooltip da tela.
  private exportScoreHeaderHtml(): string {
    const esc = (v: any) => this.escapeHtml(String(v == null ? '' : v));
    const showReviewer = !!this.vis['reviewer-name'];
    const parts: string[] = [`<span>${esc(this.translate.instant('forms.fields.status'))}</span>`];
    if (showReviewer) { parts.push(`<span class="scr">${esc(this.translate.instant('forms.fields.reviewer'))}</span>`); }
    this.scoreHeaders.forEach((h, j) => {
      // Tooltip ESTILIZADO (hover CSS, `.sg-tip`) com a legenda completa (título + valor: descrição),
      // como na tela — em vez do `title` nativo (cru/atrasado). O texto vem com \n; `white-space:
      // pre-line` no CSS preserva as quebras. (No clipboard o `.sg-tip` fica display:none inline.)
      const tip = esc(this.scoreTooltips[j] || h.full);
      parts.push(`<span><span class="sg-pill">${esc(h.initial)}<span class="sg-tip">${tip}</span></span></span>`);
    });
    parts.push(`<span>${esc(this.translate.instant('forms.fields.score-average-arithmetic'))}</span>`);
    parts.push(`<span>${esc(this.translate.instant('forms.fields.weight'))}</span>`);
    return `<div class="sg-head" style="grid-template-columns:${this.scoreGridCols}">${parts.join('')}</div>`;
  }

  // CORPO da mini-tabela de scores (grid `.sg`): SÓ as linhas de revisão + a linha de médias (o
  // cabeçalho vive no <th>, ver exportScoreHeaderHtml). Espelha a `.score-grid` da tela: Revisor só
  // quando visível (vis['reviewer-name']); status = pill quadrado colorido (cor de $review-status)
  // + `title` com o texto (ícone de fonte não embute); "Avg." abrange Status(+Revisor); nulos → "-".
  private exportScoreHtml(item: SubmissionRanking): string {
    const rank: any = item.rank;
    const reviews: any[] = rank?.byReview?.reviews || [];
    if (!reviews.length) { return ''; }
    const labels = this.scoreColumnLabels();
    const nCols = labels.length;
    const showReviewer = !!this.vis['reviewer-name'];
    const esc = (v: any) => this.escapeHtml(String(v == null ? '' : v));
    const num = (v: any) => esc(this.locNum(v));
    const none = '-';   // média nula → "-" (mesmo fallback da tela; antes era "Nenhum")
    const restSpan = nCols + 2;   // colunas após Status(+Revisor): critérios + Avg + Peso

    // Pill 16px IDÊNTICO à tela: bg/borda = cores EXATAS de lighten(cor,20%)/darken(cor,40%) (HSL,
    // pré-computadas em EXPORT_REVIEW_STATUS_PILL). Ícone = SVG inline (<use> de um <symbol> definido
    // 1× no doc; ver EXPORT_REVIEW_STATUS_SVG), dimensionado ao tamanho de fonte da tela (9px = em de
    // 1024 u) → sobrevive a e-mail. `title` com o texto do status.
    const pills = SubmissionsListTableV2Component.EXPORT_REVIEW_STATUS_PILL;
    const svgs = SubmissionsListTableV2Component.EXPORT_REVIEW_STATUS_SVG;
    const statusPill = (st: string): string => {
      if (!st) { return ''; }
      const key = st.toLowerCase();
      const p = pills[key] || { bg: '#E6E6E6', fg: '#606060' };
      const text = esc(this.translate.instant('status.' + st));
      const svg = svgs[key];
      let icon = '';
      if (svg) {
        const [, , vw, vh] = svg.vb.split(' ').map(Number);
        const k = 9 / 1024;   // 9px = tamanho de fonte do ícone na tela (mat-icon), em de 1024 u
        icon = `<svg class="scst-ico" width="${(vw * k).toFixed(1)}" height="${(vh * k).toFixed(1)}" aria-hidden="true"><use href="#rst-${key}"/></svg>`;
      }
      return `<span class="scst" style="background:${p.bg};border-color:${p.fg}" title="${text}">${icon}</span>`;
    };

    // Cada revisão = uma LINHA (.sg-row, subgrid) → sombra + arredondamento por linha, como a tela.
    const rows = reviews.map(r => {
      const stCell = `<span class="sg-rl">${statusPill(r.status)}</span>`;
      const revCell = showReviewer ? `<span class="scr">${esc(r.reviewer?.name || '')}</span>` : '';
      const inner = (!r.answers || r.answers.length === 0)
        ? `${stCell}${revCell}<span class="sg-rr" style="grid-column:span ${restSpan}"></span>`
        : `${stCell}${revCell}${labels.map((l, j) => `<span>${num(r.answers[j])}</span>`).join('')}`
          + `<span>${num(r.average?.toFixed(2))}</span><span class="sg-rr">${num(r.weight?.toFixed(2))}</span>`;
      return `<div class="sg-row">${inner}</div>`;
    }).join('');

    const footCells = labels.map((l, j) => {
      const v = rank.getReviewFieldAverage(j);
      return `<span>${v ? num(v.toFixed(2)) : esc(none)}</span>`;
    }).join('');
    const fAvg = rank.average ? num(rank.average.toFixed(2)) : esc(none);
    const fW = rank.weightedAverage ? num(rank.weightedAverage.toFixed(2)) : esc(none);
    const avgLbl = esc(this.translate.instant('forms.fields.score-average-arithmetic'));
    const foot = `<div class="sg-row sg-foot"><span class="sg-rl" style="grid-column:span ${showReviewer ? 2 : 1}">${avgLbl}</span>`
      + `${footCells}<span>${fAvg}</span><span class="sg-rr">${fW}</span></div>`;

    return `<div class="sg" style="grid-template-columns:${this.scoreGridCols}">${rows}${foot}</div>`;
  }

  // Autores: cada autor respeitando os sub-campos visíveis (nome / email / afiliação
  // / país). Separador configurável: "\n" (Excel, um por linha) ou ", " (HTML, inline).
  private exportAuthors(item: SubmissionRanking, separator: string = '\n'): string {
    const s: any = item.submission;
    return (s.authors || []).map((a: any) => {
      const u = a.user || {};
      const name = this.isSetField(['authors-names']) ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : '';
      const email = this.isSetField(['authors-emails']) && u.email ? ` <${u.email}>` : '';
      const aff = this.isSetField(['authors-affiliations']) ? (u.profile?.affiliation?.acronym || '') : '';
      const country = this.isSetField(['authors-countries']) ? (u.profile?.country || '') : '';
      const extra = [aff, country].filter(Boolean).join(', ');
      return `${name}${email}${extra ? ` (${extra})` : ''}`.trim();
    }).filter(Boolean).join(separator);
  }

  // Arquivos: texto = nomes (UM POR LINHA — wrapText no Excel); link da célula = URL
  // do PRIMEIRO arquivo enviado (o Excel aceita só 1 hyperlink por célula). Retorna
  // texto/href crus; o escape acontece em quem monta a célula.
  private exportFiles(item: SubmissionRanking): { text: string; href: string } {
    const s: any = item.submission;
    const trackId = s.track?.id;
    const trackFiles = trackId != null ? (this.getTrackFiles(trackId) || []) : [];
    const names: string[] = [];
    let href = '';
    (trackFiles as any[]).forEach((tf: any) => {
      const file: any = this.getFile(s.files, tf.id);
      const name = tf.name || file?.name || '';
      if (name) { names.push(name); }
      // Mesma razão do export em HTML: hyperlink da planilha aponta para a
      // submissão, porque o arquivo em si não é alcançável fora do app.
      if (!href && file) { href = this.submissionPageUrl(s); }
    });
    return { text: names.join('\n'), href };
  }

  // Escape para XML (SpreadsheetML): remove caracteres de controle inválidos e
  // escapa os 5 metacaracteres. Vale para conteúdo de elemento e valor de atributo.

  private orderFields(fields: string[]): string[] {	
    const intersectionResult = columnsOrder.filter(x => fields.indexOf(x) !== -1);	

    return intersectionResult;	
  }

  public addBorderColumnClass(column: string): string {
    const FIRST_COLUMN = this.fieldsShowOrdered[1];
    const LAST_COLUMN = this.fieldsShowOrdered[this.fieldsShowOrdered.length - 2];

    if (FIRST_COLUMN === column) return 'first-column';
    if (LAST_COLUMN === column) return 'last-column';

    return null;
  }

  public sortChange(sortData: {}): void {
    if (!sortData) return;

    const form = {sortBy: sortData['active'], reverseSort: sortData['direction'] === 'desc'};
    this.eventsService.putSubmissionFieldsAndFilters(this.event.id, form).subscribe();
  }

  public submitStatePublicationField(fieldID: number, submissionID: number): void {
    const data = {
      'checklist': fieldID,
      'submission': submissionID
    }

    this.publicationService.toggleSubmissionAnswer(this.event.id, data).subscribe();
  }

  public tableIsSorted(): boolean {
    return !!this.dataSource?.sort?.active;
  }

  public getTrackFiles(trackID: number): any {
    return this.event.tracks.find(track => track.id === trackID).trackFiles;
  }

  public getFile(files, trackFileID: number): File {
    return files?.find(file => file?.trackFile?.id === trackFileID);
  }

  public submitFileToSimilarityCheck(file: File) {
    this.adminService.progressBar.start();
    this.filesService.submitFileToSimilarityCheck(file).subscribe((success) => {
      this.adminService.progressBar.stop();
      if(success){
        file.submittedToVerification = true;
      } else {
        this.notificationService.notifyError(this.translate.instant(`errors.failed-file-verification`));
      }
    });
  }

  public downloadSimilarityReport(file: File) {
    this.filesService.downloadSimilarityReport(file).subscribe({
      next: (result) => {
        if (!result) {
          this.notificationService.notifyError(
            this.translate.instant('errors.failed-download-report')
          );
        }
      },
      error: () => {
        this.notificationService.notifyError(
          this.translate.instant('errors.unexpected-error')
        );
      }
    });
  }

  public rankingSpanClass(span: number): string {
    if (Number.isNaN(span)) return;

    if (span < this.eventReviewConfiguration.spanLimitA) {
      return 'span-under-A';
    } else if (span > this.eventReviewConfiguration.spanLimitB) {
      return 'span-above-B';
    }
    return 'span-between-A-and-B';
  }

  // ----- Threshold band da coluna Span -----------------------------------------------
  // Mostra o band ao passar o mouse no cabeçalho "Span"; mantém aberto enquanto o mouse
  // está sobre o cabeçalho OU sobre o popover (grace de 200 ms ao sair). Sem backdrop
  // (bloquearia o próprio hover) — o controle de abrir/fechar é todo daqui.

  public onSpanHeaderEnter(ev: MouseEvent): void {
    if (!this.eventReviewConfiguration) { return; }   // sem config carregado não há o que editar
    this.clearSpanHideTimer();
    this.spanAnchor = ev.currentTarget as HTMLElement;
    // Seta o anchor IMPERATIVAMENTE antes do show(): o binding [anchor] só propaga no
    // próximo ciclo de CD, mas show() já usa this.anchor (collectScrollParents/position)
    // no mesmo tick — sem isso, o primeiro hover abriria sem os listeners de scroll.
    if (this.spanPop) { this.spanPop.anchor = this.spanAnchor; this.spanPop.show(); }
  }

  public onSpanHeaderLeave(): void { this.scheduleSpanHide(); }
  public onSpanPopEnter(): void { this.clearSpanHideTimer(); }
  public onSpanPopLeave(): void { this.scheduleSpanHide(); }

  private scheduleSpanHide(): void {
    this.clearSpanHideTimer();
    this.spanHoverHideTimer = setTimeout(() => { if (this.spanPop) { this.spanPop.hide(); } }, 200);
  }

  private clearSpanHideTimer(): void {
    if (this.spanHoverHideTimer) { clearTimeout(this.spanHoverHideTimer); this.spanHoverHideTimer = null; }
  }

  /**
   * A cada mudança no band a coluna recolore sozinha (rankingSpanClass lê spanLimitA/B,
   * que o two-way binding já atualizou). Aqui só persistimos os novos limites no config
   * do evento, com debounce (500 ms) para não disparar um PUT por pixel de arraste.
   */
  public onSpanThresholdsChange(): void {
    if (this.spanPersistTimer) { clearTimeout(this.spanPersistTimer); }
    this.spanPersistTimer = setTimeout(() => this.persistSpanLimits(), 500);
  }

  private persistSpanLimits(): void {
    if (!this.eventReviewConfiguration || !this.event) { return; }
    const params = {
      spanLimitA: this.eventReviewConfiguration.spanLimitA,
      spanLimitB: this.eventReviewConfiguration.spanLimitB,
    };
    this.reviewConfigurationService.editReviewsConfiguration(this.event.id, params)
      .subscribe(config => { if (config) { this.eventReviewConfiguration = config; this.cdr.markForCheck(); } });
  }

  public forceHover(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    this.previousElementHovered = target.closest('tr');

    this.previousElementHovered.classList.add('hovered');
  }

  public disableHover(): void {
    this.previousElementHovered.classList.remove('hovered');
  }

  private rank(submission: Submission): Rank {
    const completedReviews = submission.reviews.filter((r: Review) => r.status === ReviewStatus.COMPLETED);

    const reviews = {};
    const resultsByReview = {};

    completedReviews.forEach((review: Review) => {
      reviews[review.id] = {};

      // Antes: `_.cloneDeep(this.reviewForm)` POR REVISÃO (deep clone do form INTEIRO, O(reviews)
      // clones — gargalo de render). Agora cada campo vira uma VIEW leve via `Object.create`:
      // herda choices/pesos/getters do campo COMPARTILHADO (read-only, iguais p/ toda revisão)
      // e ganha um `answers` PRÓPRIO (shadow) — o getter `answerForReview` lê esse `answers`.
      // Não muta o form compartilhado: o antigo `choices.sort` in-place (que só servia p/ o
      // maxValue) virou Math.max, e a ordem das choices nunca era usada (só o valor máximo).
      this.reviewForm.assignableFields.forEach(baseField => {
        const answers = review.answers.filter(a => a.formField === baseField.id && a.value != "false" && a.value != "0");
        const field = Object.create(baseField);
        field.answers = answers;

        const maxChoiceValue = Math.max(...baseField.choices.map(c => c.value));

        reviews[review.id][baseField.id] = {
          globalWeight: baseField.globalWeight,
          weight: baseField.weight,
          value: baseField.choices
                  .filter(c => answers.some(a => c.id === a.fieldChoice))
                  .reduce((acc, choice) => acc + choice.value, 0),
          maxValue: maxChoiceValue > 0 ? maxChoiceValue : 1,
          field,
          reviewer: { id: review.userId, name: review.userName }
        };
      });

      const fields: Array<FormField> = [];
      let sum_average = 0, sum_weight = 0, sum_global_weight = 0;

      let reviewerInfo;
      for (const fieldId in reviews[review.id]) {
        if (Object.prototype.hasOwnProperty.call(reviews[review.id], fieldId)) {
          const { globalWeight, weight, value, maxValue, field, reviewer }: {
            globalWeight: number,
            weight: number,
            value: number,
            maxValue: number,
            field: FormField,
            reviewer: { id: number, name: string }
          } = reviews[review.id][fieldId];

          sum_average += (value / maxValue * weight);
          sum_weight += (value / maxValue * globalWeight);
          sum_global_weight += globalWeight;
          fields.push(field);

          if (!reviewerInfo) {
            reviewerInfo = reviewer;
          }
        }
      }

      resultsByReview[review.id] = {
        average: sum_average * 10,
        weight: sum_weight,
        globalWeight: sum_global_weight,
        fields,
        reviewer: reviewerInfo
      };
    });

    const result = plainToClass(Rank, {
      average: 0,
      weightedAverage: 0,
      span: 0,
      globalWeight: 0,
      fields: [],
      byReview: {
        reviews: [],
        labels: [],
        average: [],
        weight: [],
        answers: []
      }
    });

    // Uncompleted Reviews
    const uncompletedReviews = submission.reviews.filter((r: Review) => r.status != ReviewStatus.COMPLETED);
    uncompletedReviews.forEach((review: Review) => {
      result.byReview.reviews.push({ id: review.id.toString(), reviewer: { id: review.userId, name: review.userName }, answers: [], status: review.status });
    });

    let weightedDivider = 0;
    let lowestAverage, biggestAverage;
    let lowestWeight, biggestWeight;
    for (const review in resultsByReview) {
      if (Object.prototype.hasOwnProperty.call(resultsByReview, review)) {
        const { average, weight, globalWeight, fields, reviewer } = resultsByReview[review];

        result.average += average;
        result.weightedAverage += (average * weight);
        weightedDivider += weight;

        result.globalWeight += globalWeight;

        if (average > biggestAverage || biggestAverage === undefined) {
          biggestAverage = average;
        }
        if (average < lowestAverage || lowestAverage === undefined) {
          lowestAverage = average;
        }

        if (weight > biggestWeight || biggestWeight === undefined) {
          biggestWeight = weight;
        }
        if (weight < lowestWeight || lowestWeight === undefined) {
          lowestWeight = weight;
        }

        if (result.byReview.labels.length < 1) {
          result.byReview.labels = (<FormField[]>fields).map(f => f.shortLabel);
        }

        // Setup field choice answer accumulator
        if (result.fields.length === 0) {
          result.fields = (<FormField[]>fields).map(f => {
            const fieldChoices: {
              [field: number]: {
                description: string,
                answers: number
              }
            } = {};

            f.choices.forEach(choice => {
              fieldChoices[choice.id] = { description: choice.description, answers: 0 };
            });

            return fieldChoices;
          });
        }

        const answersValue = (<FormField[]>fields).map((field, fieldIndex) => {
          const answer = (<Array<FormFieldChoice>>field.answerForReview)[0];

          // Accumulate amount of answer of this field choice
          if (answer) {
            result.fields[fieldIndex][answer.id].answers += 1;
          } else {
            return null;
          }


          return answer.value;
        });

        result.byReview.reviews.push({ id: review, reviewer: reviewer, answers: answersValue, average: average, weight: weight, status: ReviewStatus.COMPLETED });
        result.byReview.average.push(average);
        result.byReview.weight.push(weight);
        result.byReview.answers.push(answersValue);
      }
    }
    
    if (!arrayHasElements(result.byReview.labels)) {
      result.byReview.labels = this.reviewForm?.assignableFields?.map(f => f.shortLabel) || [];
    }
    result.average /= Object.keys(resultsByReview).length;

    if (result.globalWeight === 0) {
      result.weightedAverage = result.average;
    } else {
      result.weightedAverage /= weightedDivider;
    }

    result.span = biggestAverage - lowestAverage;

    result.range = {
      average: {
        high: biggestAverage,
        low: lowestAverage
      },
      weight: {
        high: biggestWeight,
        low: lowestWeight
      }
    };

    return result;
  }

  updateFields(submission: Submission) {
    this.submissionRanking.forEach((x, index) => {
      if (x.submission.id === submission.id) {
        this.submissionRanking[index].submission = submission;
      }
    });
    this.setTableSource(this.filteredSubmissions)
  }
}
